(() => {
  document.documentElement.classList.remove('no-js');
  document.documentElement.classList.add('js');

  const formatMoney = (cents) => {
    const locale = document.documentElement.lang || 'en';
    const currency = window.Shopify?.currency?.active || 'EUR';

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
    }).format((cents || 0) / 100);
  };

  const escapeHtml = (value = '') => String(value).replace(/[&<>"']/g, (char) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  }[char]));

  const singleCardActionIcons = {
    article: `
      <svg class="q-single-product__action-icon" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <rect x="5" y="4" width="14" height="16" rx="3"></rect>
        <path d="M8 9h8M8 13h5M15 4v4h4"></path>
      </svg>
    `,
    cart: `
      <svg class="q-single-product__action-icon" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M6 6h15l-2 9H8L6 3H3"></path>
        <circle cx="9" cy="20" r="1"></circle>
        <circle cx="18" cy="20" r="1"></circle>
      </svg>
    `,
  };

  const normalizePriceToCents = (price) => {
    if (price === null || typeof price === 'undefined') return 0;
    if (typeof price === 'number') return price > 1000 ? price : Math.round(price * 100);

    const value = String(price).replace(',', '.');
    if (value.includes('.')) return Math.round(Number(value) * 100);
    return Number(value) || 0;
  };

  const escapeRegExp = (value = '') => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const normalizeText = (value = '') => String(value)
    .replace(/\s+/g, ' ')
    .replace(/\s+([,;:])/g, '$1')
    .trim();

  const normalizeSearchText = (value = '') => normalizeText(value).toLowerCase();

  const splitConfiguredTerms = (value = '') => String(value)
    .split(/[\n,;|]+/)
    .map((term) => normalizeText(term))
    .filter(Boolean);

  const removeConfiguredTerms = (value = '', terms = []) => {
    let result = ` ${normalizeText(value)} `;
    const sortedTerms = [...new Set(terms.filter(Boolean))]
      .sort((first, second) => second.length - first.length);

    sortedTerms.forEach((term) => {
      const pattern = escapeRegExp(term).replace(/\s+/g, '[\\s-]+');
      result = result.replace(new RegExp(`(^|[\\s,;/|(-])${pattern}(?=$|[\\s,;/|)-])`, 'gi'), ' ');
    });

    return normalizeText(result.replace(/[|;/,]+/g, ' '));
  };

  const getProductTags = (product = {}) => {
    if (Array.isArray(product.tags)) return product.tags.map((tag) => normalizeText(tag)).filter(Boolean);
    if (typeof product.tags === 'string') return splitConfiguredTerms(product.tags);
    return [];
  };

  const getVariantTitles = (product = {}) => {
    if (!Array.isArray(product.variants)) return [];

    return product.variants
      .map((variant) => normalizeText(variant.title || variant.name || ''))
      .filter(Boolean);
  };

  const textHasConfiguredTerm = (value = '', terms = []) => {
    const text = ` ${normalizeText(value)} `;

    return terms.some((term) => {
      const pattern = escapeRegExp(term).replace(/\s+/g, '[\\s-]+');
      return new RegExp(`(^|[\\s,;/|(-])${pattern}(?=$|[\\s,;/|)-])`, 'i').test(text);
    });
  };

  const getSingleLiveSearchFilterSource = (product = {}) => [
    product.title,
    product.handle,
    product.type,
    product.product_type,
    ...Object.values(product.q_metafields || {}),
    ...Object.values(product.metafields || {}),
    ...getProductTags(product),
    ...getVariantTitles(product),
  ].filter(Boolean).join(' ');

  const passesSingleLiveSearchRules = (product = {}, whitelistTerms = [], blacklistTerms = []) => {
    const source = getSingleLiveSearchFilterSource(product);

    if (blacklistTerms.length && textHasConfiguredTerm(source, blacklistTerms)) return false;
    if (whitelistTerms.length && !textHasConfiguredTerm(source, whitelistTerms)) return false;

    return true;
  };

  const getTagValueByPrefixes = (tags = [], prefixes = []) => {
    for (const tag of tags) {
      const cleanTag = normalizeText(tag);

      for (const prefix of prefixes) {
        const cleanPrefix = normalizeText(prefix);
        if (!cleanPrefix) continue;

        const pattern = new RegExp(`^${escapeRegExp(cleanPrefix)}\\s*`, 'i');
        if (pattern.test(cleanTag)) {
          return normalizeText(cleanTag.replace(pattern, ''));
        }
      }
    }

    return '';
  };

  const findCardNumber = (value = '') => {
    const match = normalizeText(value).match(/\b([A-Z]{0,4}\d{1,4}\s*\/\s*[A-Z]{0,4}\d{1,4})\b/i);
    return match ? match[1].replace(/\s+/g, '') : '';
  };

  const inlineTcgFieldKeys = [
    'card_name',
    'card name',
    'kartenname',
    'set_name',
    'set name',
    'tcg_set',
    'tcg set',
    'collection_name',
    'kollektion',
    'erweiterung',
  ];

  const getInlineFieldKeyPattern = (key = '') => escapeRegExp(normalizeText(key)).replace(/[\s_]+/g, '[\\s_-]*');
  const inlineTcgFieldStopPattern = inlineTcgFieldKeys.map(getInlineFieldKeyPattern).join('|');
  const getInlineFieldPattern = (key = '', flags = 'i') => (
    new RegExp(`(?:^|[\\s|;,])${getInlineFieldKeyPattern(key)}\\s*[:=]\\s*([^|;,]+?)(?=\\s+(?:${inlineTcgFieldStopPattern})\\s*[:=]|$)`, flags)
  );

  const getInlineFieldValue = (value = '', keys = []) => {
    const source = normalizeText(value);
    if (!source) return '';

    for (const key of keys) {
      const pattern = getInlineFieldPattern(key);
      const match = source.match(pattern);
      if (match?.[1]) return normalizeText(match[1]);
    }

    return '';
  };

  const stripInlineFields = (value = '', keys = inlineTcgFieldKeys) => {
    let result = normalizeText(value);
    if (!result) return '';

    keys.forEach((key) => {
      result = result.replace(getInlineFieldPattern(key, 'gi'), ' ');
    });

    return normalizeText(result);
  };

  const getSetFromTags = (tags = [], cleanupTerms = [], nonSetTerms = []) => {
    const candidates = tags
      .filter((tag) => {
        if (findCardNumber(tag)) return false;
        if (textHasConfiguredTerm(tag, cleanupTerms)) return false;
        if (textHasConfiguredTerm(tag, nonSetTerms)) return false;
        return normalizeText(tag).length > 1;
      })
      .sort((first, second) => {
        const firstIsCode = /^[A-ZÄÖÜ]{2,8}$/.test(first);
        const secondIsCode = /^[A-ZÄÖÜ]{2,8}$/.test(second);
        if (firstIsCode !== secondIsCode) return firstIsCode ? 1 : -1;
        return second.length - first.length;
      });

    return candidates[0] || '';
  };

  const detectTcgVariant = (value = '', terms = {}) => {
    if (textHasConfiguredTerm(value, terms.reverse || [])) return 'reverse_holo';
    if (textHasConfiguredTerm(value, terms.rocket || [])) return 'rocket';
    if (textHasConfiguredTerm(value, terms.masterball || [])) return 'master_ball';
    if (textHasConfiguredTerm(value, terms.stamp || [])) return 'stamp';
    if (textHasConfiguredTerm(value, terms.pokeball || [])) return 'pokeball';
    if (textHasConfiguredTerm(value, terms.holo || [])) return 'holo';

    return 'normal';
  };

  const parseTcgCardTitle = (product = {}, config = {}) => {
    const title = normalizeText(product.title || '');
    const tags = config.useTags ? getProductTags(product) : [];
    const tagText = tags.join(' ');
    const inlineFieldSource = `${title} ${tagText}`;
    const inlineCardName = getInlineFieldValue(inlineFieldSource, ['card_name', 'card name', 'kartenname']);
    const inlineSetName = getInlineFieldValue(inlineFieldSource, ['set_name', 'set name', 'tcg_set', 'tcg set', 'collection_name', 'kollektion', 'erweiterung']);
    const variantTerms = {
      reverse: config.reverseTerms || [],
      holo: config.holoTerms || [],
      rocket: config.rocketTerms || [],
      pokeball: config.pokeballTerms || [],
      stamp: config.stampTerms || [],
      masterball: config.masterballTerms || [],
      normal: config.normalTerms || [],
    };
    const variant = detectTcgVariant(`${title} ${tagText}`, variantTerms);
    const cleanupTerms = [
      ...(config.hiddenTerms || []),
      ...(config.reverseTerms || []),
      ...(config.holoTerms || []),
      ...(config.normalTerms || []),
      ...(config.languageTerms || []),
      ...(config.conditionTerms || []),
      ...(config.rocketTerms || []),
      ...(config.pokeballTerms || []),
      ...(config.stampTerms || []),
      ...(config.masterballTerms || []),
    ];
    const numberMatch = title.match(/\b([A-Z]{0,4}\d{1,4}\s*\/\s*[A-Z]{0,4}\d{1,4})\b/i);
    const tagNumber = getTagValueByPrefixes(tags, config.numberTagPrefixes || '') || tags.map(findCardNumber).find(Boolean) || '';
    const number = numberMatch ? numberMatch[1].replace(/\s+/g, '') : tagNumber.replace(/\s+/g, '');
    const beforeNumber = numberMatch ? title.slice(0, numberMatch.index) : title;
    const afterNumber = numberMatch ? title.slice(numberMatch.index + numberMatch[0].length) : '';
    const tagSet = getTagValueByPrefixes(tags, config.setTagPrefixes || [])
      || (config.tagSetFallbackEnabled ? getSetFromTags(tags, cleanupTerms, config.nonSetTagTerms || []) : '');
    const fallbackSet = tagSet || product.type || product.product_type || '';
    let name = inlineCardName || removeConfiguredTerms(stripInlineFields(beforeNumber || title), cleanupTerms) || title;
    let set = inlineSetName || removeConfiguredTerms(stripInlineFields(afterNumber), cleanupTerms) || removeConfiguredTerms(stripInlineFields(fallbackSet), cleanupTerms);
    const trailingSetCode = name.match(/\s+\b([A-ZÄÖÜ]{2,8})$/);

    if (config.setCodeEnabled !== false && trailingSetCode) {
      if (!set) set = trailingSetCode[1];
      name = normalizeText(name.slice(0, trailingSetCode.index));
    }

    return {
      name,
      number,
      set,
      variant,
    };
  };

  const VACATION_NOTICE_STORAGE_KEY = 'quitchibo:vacation-notice';
  const vacationNoticeFrames = new WeakMap();
  const acknowledgedVacationMessages = new Set();
  const suppressedVacationNoticeDialogs = new WeakSet();
  let activeVacationDialog = null;

  const initVacationMarquee = (root = document) => {
    root.querySelectorAll('[data-vacation-marquee]').forEach((marquee) => {
      if (marquee.dataset.vacationMarqueeReady === 'true') return;

      const toggle = marquee.querySelector('[data-vacation-marquee-toggle]');
      const pauseIcon = marquee.querySelector('[data-vacation-marquee-pause-icon]');
      const playIcon = marquee.querySelector('[data-vacation-marquee-play-icon]');
      if (!toggle || !pauseIcon || !playIcon) return;

      marquee.dataset.vacationMarqueeReady = 'true';

      const setPaused = (paused) => {
        const label = paused ? 'Laufschrift fortsetzen' : 'Laufschrift pausieren';
        marquee.classList.toggle('is-paused', paused);
        toggle.setAttribute('aria-pressed', String(paused));
        toggle.setAttribute('aria-label', label);
        toggle.setAttribute('title', label);
        pauseIcon.toggleAttribute('hidden', paused);
        playIcon.toggleAttribute('hidden', !paused);
      };

      toggle.addEventListener('click', () => {
        setPaused(toggle.getAttribute('aria-pressed') !== 'true');
      });

      setPaused(false);
    });
  };

  const clearVacationNoticeState = (dialog) => {
    const frame = vacationNoticeFrames.get(dialog);
    if (typeof frame === 'number') window.cancelAnimationFrame(frame);
    vacationNoticeFrames.delete(dialog);
    if (activeVacationDialog === dialog) activeVacationDialog = null;
  };

  const cleanupVacationNotices = (root = document) => {
    root.querySelectorAll('[data-vacation-dialog]').forEach((dialog) => {
      suppressedVacationNoticeDialogs.add(dialog);
      clearVacationNoticeState(dialog);

      if (!dialog.open || typeof dialog.close !== 'function') return;

      try {
        dialog.close();
      } catch {}
    });
  };

  const initVacationNotice = (root = document) => {
    let hasEligibleDialog = false;

    root.querySelectorAll('[data-vacation-dialog]').forEach((dialog) => {
      if (hasEligibleDialog || dialog.dataset.vacationDialogReady === 'true') return;
      if (activeVacationDialog && activeVacationDialog !== dialog) return;

      hasEligibleDialog = true;
      dialog.dataset.vacationDialogReady = 'true';

      const message = dialog.dataset.vacationMessage || '';
      let storedMessage = null;
      let sessionStoredMessage = null;

      try {
        storedMessage = localStorage.getItem(VACATION_NOTICE_STORAGE_KEY);
      } catch {}

      try {
        sessionStoredMessage = sessionStorage.getItem(VACATION_NOTICE_STORAGE_KEY);
      } catch {}

      if (sessionStoredMessage === message || acknowledgedVacationMessages.has(message)) storedMessage = message;

      const rememberMessage = () => {
        if (window.Shopify?.designMode) return;
        if (suppressedVacationNoticeDialogs.has(dialog)) return;

        acknowledgedVacationMessages.add(message);

        try {
          localStorage.setItem(VACATION_NOTICE_STORAGE_KEY, message);
        } catch {}

        try {
          sessionStorage.setItem(VACATION_NOTICE_STORAGE_KEY, message);
        } catch {}
      };

      dialog.querySelectorAll('[data-vacation-dialog-close]').forEach((button) => {
        button.addEventListener('click', () => dialog.close());
      });

      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });
      dialog.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && dialog.open) event.stopPropagation();
      });
      dialog.addEventListener('close', rememberMessage);
      dialog.addEventListener('close', () => clearVacationNoticeState(dialog));

      const shouldOpen = window.Shopify?.designMode || storedMessage !== message;
      if (!shouldOpen || typeof dialog.showModal !== 'function') return;

      activeVacationDialog = dialog;
      const frame = window.requestAnimationFrame(() => {
        vacationNoticeFrames.delete(dialog);
        if (activeVacationDialog !== dialog) return;
        if (!dialog.isConnected || dialog.open) {
          if (!dialog.isConnected) activeVacationDialog = null;
          return;
        }

        try {
          dialog.showModal();
        } catch {
          if (activeVacationDialog === dialog) activeVacationDialog = null;
        }
      });
      vacationNoticeFrames.set(dialog, frame);
    });
  };

  const initHeaderMenu = (root) => {
    if (!root || root.dataset.headerMenuReady === 'true') return;
    root.dataset.headerMenuReady = 'true';

    const opener = root.querySelector('[data-header-menu-open]');
    const brand = root.querySelector('.q-header__brand');
    const drawer = root.querySelector('[data-header-menu-drawer]');
    const panel = root.querySelector('.q-header-menu__panel');
    let closeTimer;
    let handleFrame;

    if (!opener || !drawer || !panel) return;

    const syncMenuHandle = () => {
      if (drawer.hidden) return;

      const openerRect = opener.getBoundingClientRect();
      const brandRect = brand?.getBoundingClientRect();
      const drawerRect = drawer.getBoundingClientRect();
      const panelRect = panel.getBoundingClientRect();
      const currentShift = parseFloat(root.style.getPropertyValue('--q-header-menu-button-shift')) || 0;
      const currentLogoShift = parseFloat(root.style.getPropertyValue('--q-header-logo-shift')) || 0;
      const originLeft = openerRect.left - currentShift;
      const originRight = openerRect.right - currentShift;
      const maxShift = window.innerWidth - originRight - 8;
      const edgeShift = panelRect.right - originRight;
      const shift = Math.max(0, Math.min(edgeShift, maxShift));

      root.style.setProperty('--q-header-menu-button-shift', `${Math.round(shift)}px`);

      if (brandRect) {
        const logoOriginLeft = brandRect.left - currentLogoShift;
        const logoStopLeft = Math.max(drawerRect.left + 16, originLeft);
        const logoMaxShift = Math.max(0, logoOriginLeft - logoStopLeft);
        const menuMaxShift = Math.max(1, Math.min(drawerRect.left + panel.offsetWidth - originRight, maxShift));
        const logoProgress = Math.min(1, shift / menuMaxShift);
        const logoShift = shift > 0 ? -Math.round(logoMaxShift * logoProgress) : 0;

        root.style.setProperty('--q-header-logo-shift', `${logoShift}px`);
      }
    };

    const trackMenuHandle = (onDone) => {
      window.cancelAnimationFrame(handleFrame);

      const startedAt = window.performance.now();
      const tick = (now) => {
        syncMenuHandle();

        if (now - startedAt < 380) {
          handleFrame = window.requestAnimationFrame(tick);
          return;
        }

        syncMenuHandle();
        onDone?.();
      };

      handleFrame = window.requestAnimationFrame(tick);
    };

    const openMenu = () => {
      window.clearTimeout(closeTimer);
      drawer.hidden = false;
      syncMenuHandle();
      opener.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('q-menu-drawer-open');
      window.requestAnimationFrame(() => {
        root.classList.remove('is-menu-closing');
        root.classList.add('is-menu-open');
        trackMenuHandle();
        panel.focus();
      });
    };

    const closeMenu = () => {
      window.clearTimeout(closeTimer);
      opener.setAttribute('aria-expanded', 'false');
      root.classList.add('is-menu-closing');
      root.classList.remove('is-menu-open');
      document.documentElement.classList.remove('q-menu-drawer-open');
      trackMenuHandle(() => {
        root.classList.remove('is-menu-closing');
        root.style.removeProperty('--q-header-menu-button-shift');
        root.style.removeProperty('--q-header-logo-shift');
      });
      closeTimer = window.setTimeout(() => {
        drawer.hidden = true;
        window.cancelAnimationFrame(handleFrame);
      }, 420);
      opener.focus({ preventScroll: true });
    };

    opener.addEventListener('click', () => {
      if (root.classList.contains('is-menu-open')) {
        closeMenu();
      } else {
        openMenu();
      }
    });

    drawer.addEventListener('click', (event) => {
      const closeTarget = event.target.closest('[data-header-menu-close], [data-header-menu-link]');
      if (closeTarget || !panel.contains(event.target)) closeMenu();
    });

    document.addEventListener('click', (event) => {
      if (!root.classList.contains('is-menu-open')) return;
      if (opener.contains(event.target) || panel.contains(event.target)) return;

      event.preventDefault();
      closeMenu();
    }, true);

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && root.classList.contains('is-menu-open')) closeMenu();
    });

    window.addEventListener('resize', () => {
      if (root.classList.contains('is-menu-open') || root.classList.contains('is-menu-closing')) syncMenuHandle();
    });
  };

  const initHeroHeadingBuild = (root = document) => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const scope = root instanceof Document || root instanceof Element ? root : document;

    scope.querySelectorAll('.q-hero--intro-fly .q-hero__heading').forEach((heading) => {
      if (heading.dataset.heroHeadingReady === 'true') return;

      const lines = Array.from(heading.querySelectorAll('.q-hero__heading-base[data-text], .q-hero__heading-accent[data-text]'));
      if (!lines.length) return;

      let builtAnyLine = false;
      let maxFinishDelay = 0;

      lines.forEach((line) => {
        if (line.dataset.heroHeadingLineReady === 'true') return;

        const text = normalizeText(line.dataset.text || line.textContent || '');
        if (!text) return;

        const overlay = document.createElement('span');
        overlay.className = 'q-hero__letters';
        overlay.setAttribute('aria-hidden', 'true');

        let charIndex = 0;

        text.split(' ').forEach((word) => {
          const wordElement = document.createElement('span');
          wordElement.className = 'q-hero__word';

          Array.from(word).forEach((character) => {
            const charElement = document.createElement('span');
            const xOffset = ((charIndex % 5) - 2) * 0.18;
            const yOffset = charIndex % 2 === 0 ? -0.95 : 0.95;
            const rotation = ((charIndex % 7) - 3) * 5;

            charElement.className = 'q-hero__char';
            charElement.textContent = character;
            charElement.style.setProperty('--q-hero-char-index', charIndex);
            charElement.style.setProperty('--q-hero-char-x', `${xOffset.toFixed(2)}em`);
            charElement.style.setProperty('--q-hero-char-y', `${yOffset.toFixed(2)}em`);
            charElement.style.setProperty('--q-hero-char-rotate', `${rotation}deg`);
            wordElement.append(charElement);

            charIndex += 1;
          });

          overlay.append(wordElement);
        });

        const isAccentLine = line.classList.contains('q-hero__heading-accent');
        const stagger = isAccentLine ? 36 : 40;
        const lineDelay = isAccentLine ? 480 : 240;
        const charDuration = isAccentLine ? 980 : 940;
        const finishDelay = lineDelay + (Math.max(charIndex - 1, 0) * stagger) + charDuration;

        line.classList.add('q-hero__heading-line--animated');
        line.dataset.heroHeadingLineReady = 'true';
        line.style.setProperty('--q-hero-char-duration', `${charDuration}ms`);
        line.style.setProperty('--q-hero-char-stagger', `${stagger}ms`);
        line.style.setProperty('--q-hero-line-delay', `${lineDelay}ms`);
        line.append(overlay);
        builtAnyLine = true;
        maxFinishDelay = Math.max(maxFinishDelay, finishDelay);
      });

      if (builtAnyLine) {
        heading.classList.add('q-hero__heading--chars');
        heading.dataset.heroHeadingReady = 'true';
        window.setTimeout(() => {
          heading.classList.add('q-hero__heading--built');
        }, maxFinishDelay + 80);
      }
    });
  };

  const getHeroBannerTitleLines = (heading) => {
    const lines = [''];

    heading.childNodes.forEach((node) => {
      if (node.nodeName === 'BR') {
        lines.push('');
        return;
      }

      lines[lines.length - 1] += node.textContent || '';
    });

    const cleanedLines = lines.map((line) => line.replace(/\s+/g, ' ').trim());
    return cleanedLines.some(Boolean) ? cleanedLines : [];
  };

  const resolveHeroBannerIntroSplit = (hero, lines) => {
    const configuredSplit = hero.dataset.heroBannerIntroSplit || 'auto';
    const reduceMobile = hero.dataset.heroBannerIntroReduceMobile !== 'false';
    const isMobile = window.matchMedia?.('(max-width: 749px)').matches;
    const characterCount = lines.join('').replace(/\s/g, '').length;

    if (configuredSplit === 'line') return 'line';
    if (configuredSplit === 'word') return 'word';
    if (configuredSplit === 'char') {
      if (reduceMobile && isMobile && characterCount > 18) return 'word';
      if (characterCount > 64) return 'word';
      return 'char';
    }

    if (reduceMobile && isMobile) return 'word';
    return characterCount <= 34 ? 'char' : 'word';
  };

  const setHeroBannerIntroTokenMotion = (token, index, count, hero) => {
    const direction = hero.dataset.heroBannerIntroDirection || 'up';
    const strength = hero.dataset.heroBannerIntroStrength || 'normal';
    const distanceByStrength = {
      subtle: 0.42,
      normal: 0.72,
      strong: 1,
    };
    const scaleByStrength = {
      subtle: 0.97,
      normal: 0.94,
      strong: 0.91,
    };
    const distance = distanceByStrength[strength] || distanceByStrength.normal;
    const midpoint = Math.max(0.5, (count - 1) / 2);
    let x = 0;
    let y = 0;

    if (direction === 'left') {
      x = -distance * 1.15;
      y = ((index % 3) - 1) * distance * 0.08;
    } else if (direction === 'right') {
      x = distance * 1.15;
      y = ((index % 3) - 1) * distance * 0.08;
    } else if (direction === 'center-out') {
      x = index <= midpoint ? distance * 0.74 : -distance * 0.74;
      y = distance * 0.14;
    } else {
      x = ((index % 3) - 1) * distance * 0.06;
      y = distance;
    }

    token.style.setProperty('--q-hero-banner-intro-index', index);
    token.style.setProperty('--q-hero-banner-intro-x', `${x.toFixed(3)}em`);
    token.style.setProperty('--q-hero-banner-intro-y', `${y.toFixed(3)}em`);
    token.style.setProperty('--q-hero-banner-intro-scale', scaleByStrength[strength] || scaleByStrength.normal);
  };

  const makeHeroBannerIntroToken = (text, index) => {
    const token = document.createElement('span');
    token.className = 'q-hero-banner__intro-token';
    token.textContent = text;
    token.dataset.heroBannerIntroToken = String(index);
    return token;
  };

  const buildHeroBannerIntroLine = (line, split, tokenState) => {
    const lineElement = document.createElement('span');
    lineElement.className = 'q-hero-banner__intro-line';

    if (split === 'line') {
      const token = makeHeroBannerIntroToken(line, tokenState.index);
      tokenState.tokens.push(token);
      tokenState.index += 1;
      lineElement.append(token);
      return lineElement;
    }

    if (split === 'char') {
      Array.from(line).forEach((character) => {
        if (/\s/.test(character)) {
          lineElement.append(document.createTextNode(character));
          return;
        }

        const token = makeHeroBannerIntroToken(character, tokenState.index);
        tokenState.tokens.push(token);
        tokenState.index += 1;
        lineElement.append(token);
      });
      return lineElement;
    }

    (line.match(/\S+|\s+/g) || []).forEach((part) => {
      if (/^\s+$/.test(part)) {
        lineElement.append(document.createTextNode(part));
        return;
      }

      const token = makeHeroBannerIntroToken(part, tokenState.index);
      tokenState.tokens.push(token);
      tokenState.index += 1;
      lineElement.append(token);
    });

    return lineElement;
  };

  const getHeroBannerTitleFontShorthand = (heading) => {
    const computed = window.getComputedStyle(heading);
    const matchedFont = (computed.font || '').trim();
    if (matchedFont) return matchedFont;

    const fontStyle = computed.fontStyle || 'normal';
    const fontWeight = computed.fontWeight || '400';
    const fontSize = computed.fontSize;
    const fontFamily = computed.fontFamily;
    if (!fontSize || !fontFamily) return '';

    return `${fontStyle} ${fontWeight} ${fontSize} ${fontFamily}`;
  };

  const isMultilineHeroBannerTitle = (heading) => {
    const computed = window.getComputedStyle(heading);
    const parsedLineHeight = Number.parseFloat(computed.lineHeight);
    const fontSize = Number.parseFloat(computed.fontSize);
    const lineHeight = parsedLineHeight > 0 ? parsedLineHeight : fontSize * 1.2;
    const titleHeight = heading.getBoundingClientRect().height;
    return lineHeight > 0 && titleHeight > lineHeight * 1.25;
  };

  const initHeroBannerIntro = (root = document) => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const scope = root instanceof Document || root instanceof Element ? root : document;

    scope.querySelectorAll('[data-hero-banner-intro="true"]').forEach((hero) => {
      if (hero.dataset.heroBannerIntroReady === 'true') return;

      if (hero.dataset.heroBannerIntroSplit === 'group') {
        hero.dataset.heroBannerIntroReady = 'true';
        return;
      }

      const heading = hero.querySelector('.q-hero-banner__title');
      if (!heading) return;

      const isNarrowLayout = window.matchMedia('(max-width: 749px)').matches;
      if (isNarrowLayout && document.fonts && typeof document.fonts.load === 'function' && hero.dataset.heroBannerIntroFontSettled !== 'true') {
        const fontShorthand = getHeroBannerTitleFontShorthand(heading);
        const headingText = heading.textContent || '';

        if (fontShorthand) {
          if (hero.dataset.heroBannerIntroPending === 'true') return;

          hero.dataset.heroBannerIntroPending = 'true';
          document.fonts.load(fontShorthand, headingText)
            .catch(() => {})
            .finally(() => {
              delete hero.dataset.heroBannerIntroPending;
              hero.dataset.heroBannerIntroFontSettled = 'true';
              if (!hero.isConnected) return;
              initHeroBannerIntro(hero.parentElement || document);
            });
          return;
        }
      }

      if (isNarrowLayout && isMultilineHeroBannerTitle(heading)) {
        hero.dataset.heroBannerIntroReady = 'true';
        return;
      }

      const originalHtml = heading.innerHTML;
      const originalText = normalizeText(heading.textContent || '');
      const lines = getHeroBannerTitleLines(heading);
      if (!originalText || !lines.length) return;

      const split = resolveHeroBannerIntroSplit(hero, lines);
      const original = document.createElement('span');
      const intro = document.createElement('span');
      const tokenState = { index: 0, tokens: [] };

      original.className = 'q-hero-banner__title-original';
      original.setAttribute('aria-hidden', 'true');
      original.innerHTML = originalHtml;

      intro.className = `q-hero-banner__intro q-hero-banner__intro--${split}`;
      intro.setAttribute('aria-hidden', 'true');

      lines.forEach((line) => {
        intro.append(buildHeroBannerIntroLine(line, split, tokenState));
      });

      if (!tokenState.tokens.length) return;

      tokenState.tokens.forEach((token, index) => {
        setHeroBannerIntroTokenMotion(token, index, tokenState.tokens.length, hero);
      });

      heading.innerHTML = '';
      heading.setAttribute('aria-label', originalText);
      heading.append(original);
      heading.append(intro);
      heading.classList.add('is-intro-active', `is-intro-${split}`);
      hero.classList.add('is-intro-ready');
      hero.dataset.heroBannerIntroReady = 'true';

      const computed = window.getComputedStyle(hero);
      const duration = parseFloat(computed.getPropertyValue('--q-hero-banner-intro-duration')) || 1050;
      const stagger = parseFloat(computed.getPropertyValue('--q-hero-banner-intro-stagger')) || 55;
      const finishDelay = duration + ((tokenState.tokens.length - 1) * stagger) + 180;

      window.setTimeout(() => {
        heading.classList.add('is-intro-complete');

        window.setTimeout(() => {
          heading.innerHTML = originalHtml;
          heading.removeAttribute('aria-label');
          heading.classList.remove('is-intro-active', 'is-intro-line', 'is-intro-word', 'is-intro-char', 'is-intro-complete');
          hero.classList.remove('is-intro-ready');
        }, 220);
      }, finishDelay);
    });
  };

  const syncHeroHeadingFit = (hero) => {
    const heading = hero.querySelector('.q-hero__heading');
    if (!heading) return;

    heading.style.removeProperty('--q-hero-heading-fit-size');

    const availableWidth = Math.max(240, Math.min(hero.clientWidth - 32, window.innerWidth - 32));
    const currentSize = parseFloat(window.getComputedStyle(heading).fontSize);
    if (!availableWidth || !currentSize) return;

    let scale = 1;
    heading.querySelectorAll('.q-hero__heading-line').forEach((line) => {
      const lineWidth = line.scrollWidth;
      if (lineWidth > availableWidth) scale = Math.min(scale, availableWidth / lineWidth);
    });

    if (scale < 1) {
      const fittedSize = Math.max(22, Math.floor(currentSize * scale));
      heading.style.setProperty('--q-hero-heading-fit-size', `${fittedSize}px`);
    }
  };

  const syncHeroBrandMark = (hero) => {
    syncHeroHeadingFit(hero);

    const mark = hero.querySelector('[data-hero-brand-mark]');
    const heading = hero.querySelector('.q-hero__heading');
    if (!mark || !heading) return;

    const heroRect = hero.getBoundingClientRect();
    const headingRect = heading.getBoundingClientRect();
    if (!heroRect.height || !headingRect.height) return;

    const topGap = parseFloat(mark.style.getPropertyValue('--q-hero-brand-top-gap')) || 12;
    const availableToHeading = Math.max(90, headingRect.top - heroRect.top);
    const usableHeight = Math.max(72, availableToHeading - (topGap * 2));
    const anchorY = mark.classList.contains('q-hero__brand-mark--position-top')
      ? topGap
      : topGap + (usableHeight / 2);

    hero.style.setProperty('--q-hero-brand-anchor-y', `${Math.round(anchorY)}px`);
    hero.style.setProperty('--q-hero-brand-available-height', `${Math.round(usableHeight)}px`);
  };

  let heroBrandFrame = null;

  const syncHeroBrandMarks = (root = document) => {
    const scope = root instanceof Document || root instanceof Element ? root : document;
    scope.querySelectorAll('.q-hero--reference').forEach(syncHeroBrandMark);
  };

  const scheduleHeroBrandMarkSync = (root = document) => {
    if (heroBrandFrame) window.cancelAnimationFrame(heroBrandFrame);
    heroBrandFrame = window.requestAnimationFrame(() => {
      heroBrandFrame = null;
      syncHeroBrandMarks(root);
    });
  };

  const initHeroBrandMarks = (root = document) => {
    const scope = root instanceof Document || root instanceof Element ? root : document;
    scope.querySelectorAll('.q-hero--reference').forEach((hero) => {
      if (hero.dataset.heroBrandReady !== 'true') {
        hero.dataset.heroBrandReady = 'true';
        hero.querySelectorAll('.q-hero__brand-image').forEach((image) => {
          image.addEventListener('load', () => scheduleHeroBrandMarkSync(document), { once: true });
        });
      }
    });

    scheduleHeroBrandMarkSync(document);
  };

  const drawer = document.querySelector('[data-cart-drawer]');
  const drawerBody = drawer?.querySelector('[data-cart-drawer-body]');
  const drawerFooter = drawer?.querySelector('[data-cart-drawer-footer]');
  const drawerSubtotal = drawer?.querySelector('[data-cart-drawer-subtotal]');
  const drawerPanel = drawer?.querySelector('.q-cart-drawer__panel');
  const drawerProgress = drawer?.querySelector('[data-cart-progress]');
  const drawerProgressText = drawer?.querySelector('[data-cart-progress-text]');

  const renderShippingProgressText = (target, template, amount, completeText, isComplete) => {
    if (!target) return;

    target.textContent = '';

    if (isComplete) {
      target.textContent = completeText;
      return;
    }

    if (!template.includes('[amount]')) {
      target.textContent = template;
      return;
    }

    const parts = template.split('[amount]');
    parts.forEach((part, index) => {
      if (part) target.append(document.createTextNode(part));

      if (index < parts.length - 1) {
        const amountNode = document.createElement('strong');
        amountNode.textContent = amount;
        target.append(amountNode);
      }
    });
  };

  const getHeaderShippingThreshold = () => {
    const headerProgress = document.querySelector('[data-shipping-progress]');
    return Number(headerProgress?.dataset.threshold || 0);
  };

  const getSharedShippingThreshold = (progressRoot) => getHeaderShippingThreshold() || Number(progressRoot?.dataset.threshold || 0);

  const updateShippingProgressElement = (progressRoot, cart, options = {}) => {
    if (!progressRoot || progressRoot.hidden) return;

    const threshold = getSharedShippingThreshold(progressRoot);
    if (!threshold) return;

    const total = Number(cart?.total_price ?? progressRoot.dataset.currentTotal ?? 0);
    const progress = Math.min(100, (total / threshold) * 100).toFixed(2);
    const isComplete = total >= threshold;
    const remainingTemplate = progressRoot.dataset.remainingTemplate || options.remainingTemplate || 'Noch [amount] bis kostenloser Versand';
    const completeText = progressRoot.dataset.completeText || options.completeText || 'Kostenloser Versand freigeschaltet';
    const textTarget = progressRoot.querySelector(options.textSelector || '[data-shipping-progress-text]');
    const track = progressRoot.querySelector(options.trackSelector || '[data-shipping-progress-track]');
    const progressVar = options.progressVar || '--q-shipping-progress';

    progressRoot.dataset.threshold = String(threshold);
    progressRoot.dataset.currentTotal = String(total);
    progressRoot.style.setProperty(progressVar, `${progress}%`);
    progressRoot.classList.toggle('is-complete', isComplete);
    track?.setAttribute('aria-valuenow', String(total));
    track?.setAttribute('aria-valuemax', String(threshold));

    renderShippingProgressText(
      textTarget,
      remainingTemplate,
      formatMoney(Math.max(0, threshold - total)),
      completeText,
      isComplete
    );
  };

  const updateHeaderShippingProgress = (cart) => {
    document.querySelectorAll('[data-shipping-progress], [data-cart-page-progress]').forEach((progressRoot) => {
      const isCartPageProgress = progressRoot.matches('[data-cart-page-progress]');
      updateShippingProgressElement(progressRoot, cart, {
        progressVar: isCartPageProgress ? '--q-cart-progress' : '--q-shipping-progress',
        textSelector: isCartPageProgress ? '[data-cart-progress-text]' : '[data-shipping-progress-text]',
        trackSelector: isCartPageProgress ? '[data-cart-progress-track]' : '[data-shipping-progress-track]',
        remainingTemplate: isCartPageProgress ? 'Noch [amount] bis zum kostenlosen Versand' : 'Noch [amount] bis kostenloser Versand',
      });
    });
  };

  const syncShippingProgressFromDom = () => {
    updateHeaderShippingProgress();
  };

  const renderLineItemProperties = (properties) => {
    const entries = Object.entries(properties || {}).filter(([name, value]) => (
      value && !String(name).startsWith('_')
    ));

    if (!entries.length) return '';

    return `
      <dl class="q-cart-drawer__item-properties">
        ${entries.map(([name, value]) => `
          <div>
            <dt>${escapeHtml(name)}</dt>
            <dd>${escapeHtml(value)}</dd>
          </div>
        `).join('')}
      </dl>
    `;
  };

  const updateCartProgress = (cart) => {
    if (!drawerProgress || !drawerProgressText || drawerProgress.hidden) return;

    updateShippingProgressElement(drawerProgress, cart, {
      progressVar: '--q-cart-progress',
      textSelector: '[data-cart-progress-text]',
      trackSelector: '[data-cart-progress-track]',
      remainingTemplate: 'Noch [amount] bis zum kostenlosen Versand',
    });
  };

  const renderCartDrawer = (cart) => {
    document.querySelectorAll('[data-cart-count]').forEach((target) => {
      target.textContent = cart.item_count;
    });

    updateHeaderShippingProgress(cart);

    if (!drawerBody || !drawerFooter || !drawerSubtotal) return;

    if (!cart.items.length) {
      drawerBody.innerHTML = `
        <div class="q-cart-drawer__empty">
          <h3>${escapeHtml(window.QuitchiboCart?.empty || 'Dein Warenkorb ist leer.')}</h3>
          <p>${escapeHtml(window.QuitchiboCart?.emptyText || '')}</p>
        </div>
      `;
      drawerFooter.hidden = true;
      return;
    }

    drawerBody.innerHTML = cart.items.map((item, index) => `
      <article class="q-cart-drawer__item">
        <a class="q-cart-drawer__item-media" href="${escapeHtml(item.url)}">
          ${item.image ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.product_title)}" loading="lazy">` : '<div class="q-product-card__placeholder q-product-card__placeholder--pack"><span></span><span></span><span></span></div>'}
        </a>
        <div class="q-cart-drawer__item-details">
          <a href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>
          ${item.variant_title ? `<span>${escapeHtml(item.variant_title)}</span>` : ''}
          ${renderLineItemProperties(item.properties)}
          <div class="q-cart-drawer__item-meta">
            <small>${escapeHtml(item.quantity)} x ${formatMoney(item.final_price)}</small>
            <button class="q-cart-drawer__remove" type="button" data-cart-remove data-line-key="${escapeHtml(item.key)}" data-line-index="${index + 1}" aria-label="${escapeHtml(window.QuitchiboCart?.remove || 'Entfernen')} ${escapeHtml(item.product_title)}">
              <span>${escapeHtml(window.QuitchiboCart?.remove || 'Entfernen')}</span>
            </button>
          </div>
        </div>
      </article>
    `).join('');

    drawerSubtotal.textContent = formatMoney(cart.total_price);
    updateCartProgress(cart);
    drawerFooter.hidden = false;
  };

  const removeCartItem = async (button) => {
    if (!button?.dataset.lineKey || button.dataset.cartRemoving === 'true') return;

    const originalText = button.textContent;
    const lineIndex = Number(button.dataset.lineIndex || 0);
    button.disabled = true;
    button.dataset.cartRemoving = 'true';
    button.setAttribute('aria-busy', 'true');
    button.textContent = window.QuitchiboCart?.removing || 'Wird entfernt...';

    try {
      renderCartDrawer(await changeCartLine({ id: button.dataset.lineKey, quantity: 0 }));
    } catch {
      try {
        if (!lineIndex) throw new Error('Missing cart line index.');
        renderCartDrawer(await changeCartLine({ line: lineIndex, quantity: 0 }));
      } catch {
        button.disabled = false;
        delete button.dataset.cartRemoving;
        button.removeAttribute('aria-busy');
        button.textContent = originalText;
      }
    }
  };

  const fetchCart = async () => {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
      headers: { Accept: 'application/json' },
    });
    return response.json();
  };

  const addToCart = async (form) => {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: new FormData(form),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.description || error.message || 'Produkt konnte nicht in den Warenkorb gelegt werden.');
    }

    return response.json();
  };

  const changeCartLine = async ({ id, line, quantity }) => {
    const payload = line ? { line, quantity } : { id, quantity };
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.description || error.message || 'Warenkorb konnte nicht aktualisiert werden.');
    }

    return response.json();
  };

  const normalizeFreshCatalogProduct = (previous, fresh, variantId = '') => {
    if (!previous || !fresh || String(previous.id) !== String(fresh.id) || previous.handle !== fresh.handle || !Array.isArray(fresh.variants)) return null;
    if (variantId && !fresh.variants.some((variant) => String(variant.id) === String(variantId))) return null;
    const money = (value) => value == null ? null : (Number.isFinite(Number(value)) ? (Number(value) / 100).toFixed(2) : null);
    const variants = fresh.variants.map((variant) => ({
      ...(previous.variants || []).find((old) => String(old.id) === String(variant.id)),
      ...variant, price: money(variant.price), compare_at_price: money(variant.compare_at_price),
    }));
    if (variants.some((variant) => variant.price === null)) return null;
    return { ...previous, variants, available: fresh.available, price: money(fresh.price), updated_at: fresh.updated_at || previous.updated_at };
  };

  const catalogDirtyPrefix = 'q-catalog-search:dirty:';
  const rememberCatalogCartChange = (handle) => {
    if (!handle) return;
    try {
      const storage = window.sessionStorage;
      Object.keys(storage).filter((key) => key.startsWith('q-catalog-search:active:')).forEach((key) => {
        const dirtyKey = catalogDirtyPrefix + key.slice('q-catalog-search:active:'.length);
        const previous = JSON.parse(storage.getItem(dirtyKey) || '{}');
        previous[handle] = Date.now();
        const latest = Object.entries(previous).sort((a, b) => b[1] - a[1]).slice(0, 100);
        storage.setItem(dirtyKey, JSON.stringify(Object.fromEntries(latest)));
      });
    } catch { /* A blocked session store must not prevent checkout/cart use. */ }
  };

  const initSingleCards = (root) => {
    if (!root || !root.dataset.baseHandle?.trim() || root.dataset.singleCardsReady === 'true') return;
    root.dataset.singleCardsReady = 'true';

    const panel = root.querySelector('[data-single-panel]');
    const products = root.querySelector('[data-single-products]');
    const productsFooter = root.querySelector('[data-single-products-footer]');
    const progressTarget = root.querySelector('[data-single-products-progress]');
    const loadMoreButton = root.querySelector('[data-single-load-more]');
    const soldOutToggle = root.querySelector('[data-single-sold-out-toggle]');
    const panelName = root.querySelector('[data-single-panel-name]');
    const badge = root.querySelector('[data-single-panel-badge]');
    const closeButton = root.querySelector('[data-single-close]');
    const searchInput = root.querySelector('[data-single-search]');
    const clearButton = root.querySelector('[data-single-clear]');
    const liveSearch = root.querySelector('[data-single-live-search]');
    const liveSearchResults = root.querySelector('[data-single-live-results]');
    const liveSearchStatus = root.querySelector('[data-single-live-status]');
    const liveSearchMore = root.querySelector('[data-single-live-more]');
    const gridEmpty = root.querySelector('[data-single-grid-empty]');
    const countTarget = root.querySelector('[data-single-count]');
    const buttons = Array.from(root.querySelectorAll('[data-single-set]'));
    const chips = Array.from(root.querySelectorAll('[data-single-filter]'));
    const setsToggle = root.querySelector('[data-single-sets-toggle]');
    const setsPanel = root.querySelector('[data-single-sets-panel]');
    const categoryToggles = Array.from(root.querySelectorAll('[data-single-set-category-toggle]'));
    const categoryPanels = Array.from(root.querySelectorAll('[data-single-set-category-panel]'));
    const categoryGroups = Array.from(root.querySelectorAll('[data-single-set-category-group]'));
    const sortSelect = root.querySelector('[data-single-sort]');
    const totalSets = buttons.length;
    const labels = {
      loading: root.dataset.loadingText || 'Karten werden geladen...',
      empty: root.dataset.emptyText || 'Keine Karten gefunden.',
      emptyFilter: root.dataset.emptyFilterText || 'Keine Karten mit diesem Filter gefunden.',
      error: root.dataset.errorText || 'Fehler beim Laden.',
      liveLoading: root.dataset.liveSearchLoadingText || 'Karten werden gesucht...',
      liveEmpty: root.dataset.liveSearchEmptyText || 'Keine passende Karte gefunden.',
      liveError: root.dataset.liveSearchErrorText || 'Live-Suche konnte nicht geladen werden.',
      liveMore: root.dataset.liveSearchMoreText || 'Alle Treffer ansehen',
      liveCount: root.dataset.liveSearchCountLabel || 'Vorschläge',
      setSingular: root.dataset.setSingularText || 'Set',
      setPlural: root.dataset.setPluralText || 'Sets',
      cardSingular: root.dataset.cardSingularText || 'Karte',
      cardPlural: root.dataset.cardPluralText || 'Karten',
      visibleSets: root.dataset.visibleSetsTemplate || '[visible] von [total] Sets',
      visibleProducts: root.dataset.visibleProductsTemplate || '[visible] von [total] Karten angezeigt',
      visibleHits: root.dataset.visibleHitsTemplate || '[visible] von [total] Treffern angezeigt',
      loadMore: root.dataset.loadMoreText || 'Mehr anzeigen',
      loadMoreLoading: root.dataset.loadMoreLoadingText || 'Laedt...',
      loadMoreComplete: root.dataset.loadMoreCompleteText || 'Alle Karten geladen',
    };
    const compactCartLabel = (label) => {
      const value = (label || '').trim();
      return value.toLocaleLowerCase('de-DE') === 'in den warenkorb' || !value ? 'Warenkorb' : value;
    };
    const settings = {
      productPageSize: Math.max(1, Math.min(200, Math.round(Number(root.dataset.productPageSize) || 32))),
      showSoldOutProductsByDefault: root.dataset.showSoldOutProductsByDefault === 'true',
      productDefaultSort: root.dataset.productDefaultSort || 'price-desc',
      baseProductDefaultSort: root.dataset.baseProductDefaultSort || 'price-desc',
      setPreloadLimit: Math.max(4, Math.min(40, Math.round(Number(root.dataset.setPreloadLimit) || 16))),
      autoload: root.dataset.singleAutoload !== 'false',
      scroll: root.dataset.singleScroll !== 'false',
      tilt: root.dataset.singleTilt !== 'false',
      particles: root.dataset.singleParticles !== 'false',
      singleEffectIntensity: root.dataset.singleEffectIntensity || 'balanced',
      liveSearchEnabled: root.dataset.liveSearchEnabled !== 'false',
      liveSearchLimit: Math.min(30, Math.max(3, Number(root.dataset.liveSearchLimit || 20))),
      liveSearchWhitelistTerms: splitConfiguredTerms(root.dataset.liveSearchWhitelistTerms || ''),
      liveSearchBlacklistTerms: splitConfiguredTerms(root.dataset.liveSearchBlacklistTerms || ''),
      productCardTitleSource: root.dataset.productCardTitleSource || 'title',
      productCardBuyLabel: compactCartLabel(root.dataset.productCardBuyLabel || 'Warenkorb'),
      productCardArticleLabel: root.dataset.productCardArticleLabel || 'Zum Artikel',
      productCardCloseLabel: root.dataset.productCardCloseLabel || 'Karte schliessen',
      productCardPrevLabel: root.dataset.productCardPrevLabel || 'Vorherige Karte',
      productCardNextLabel: root.dataset.productCardNextLabel || 'Naechste Karte',
      productCardMotionDuration: Math.min(1600, Math.max(400, Number(root.dataset.productCardMotionDuration || 700))),
      productCardFields: [1, 2, 3, 4].map((index) => ({
        label: root.dataset[`productCardField${index}Label`] || `Meta-Feld ${index}`,
        source: root.dataset[`productCardField${index}Source`] || 'none',
      })),
    };
    root.style.setProperty('--q-single-product-motion-duration', `${settings.productCardMotionDuration}ms`);
    root.style.setProperty('--q-single-product-info-open-delay', `${Math.min(680, Math.max(300, Math.round(settings.productCardMotionDuration * 0.46)))}ms`);
    root.style.setProperty('--q-single-product-info-open-duration', `${Math.min(720, Math.max(340, Math.round(settings.productCardMotionDuration * 0.44)))}ms`);
    root.style.setProperty('--q-single-product-info-close-duration', `${Math.min(520, Math.max(260, Math.round(settings.productCardMotionDuration * 0.34)))}ms`);
    const liveSearchIcons = {
      holo: root.dataset.liveIconHolo || '',
      reverse_holo: root.dataset.liveIconReverseHolo || '',
      rocket: root.dataset.liveIconRocket || '',
      pokeball: root.dataset.liveIconPokeball || '',
      stamp: root.dataset.liveIconStamp || '',
      master_ball: root.dataset.liveIconMasterBall || '',
    };
    const particleCountByIntensity = {
      subtle: 5,
      balanced: 10,
      strong: 16,
    };
    const tiltByIntensity = {
      subtle: 10,
      balanced: 18,
      strong: 24,
    };

    let activeHandle = null;
    let activeButton = null;
    let activeProductIndex = null;
    let allProducts = [];
    let visibleProductCount = settings.productPageSize;
    let currentSort = sortSelect?.value || settings.productDefaultSort;
    let currentProductSearchTerm = searchInput?.value || '';
    let showSoldOutProducts = settings.showSoldOutProductsByDefault;
    let liveSearchTimer;
    let liveSearchRequestId = 0;
    let catalog;
    let catalogSource;
    let catalogRestore;
    let catalogContext;
    let catalogWasRestored = false;
    let singleCardsDisposed = false;
    let preparedProductQuery = null;
    let productDataPage = 1;
    let productDataHasMore = false;
    let productDataIsLoadingMore = false;
    let productDataLoadPromise = null;
    let productDataLoadPromiseToken = 0;
    let productDataSourceReliable = true;
    let activeTotalCount = Number(root.dataset.baseTotalCount || 0) || 0;
    let activeKnownComplete = false;
    let searchDataLoadTimer;
    let searchDataLoadRequestId = 0;
    let productRenderPending = false;
    let productRenderFlushFrame = null;
    let productSwitchTimer;
    let activeCollectionRequestToken = 0;
    const productCloseTimers = new Map();
    const cardTypeFilterFeedbackDuration = 310;
    let activeCardType = '';
    let activeIllustrators = new Set();
    const illustratorCore = window.QCatalogIllustratorCore;
    let cardTypeRenderTimer;
    let sortCache = new WeakMap();

    const setBadge = (text) => {
      if (badge) badge.textContent = text;
    };

    const productTags = (product) => {
      if (Array.isArray(product.tags)) return product.tags.join(' ');
      return product.tags || '';
    };

    const formatCount = (count, singular, plural) => `${count} ${count === 1 ? singular : plural}`;

    const splitTerms = (value) => (value || '')
      .toLowerCase()
      .split(',')
      .map((term) => term.trim())
      .filter(Boolean);

    const normalizeSingleCardSearchQuery = (value = '') => normalizeSearchText(String(value)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/ß/g, 'ss')
      .replace(/[._/\\|+]+/g, ' ')
      .replace(/[-]+/g, ' ')
      .replace(/[^\w\s#]+/g, ' '))
      .replace(/\s+/g, ' ')
      .trim();

    const compactSingleCardSearchQuery = (value = '') => normalizeSingleCardSearchQuery(value).replace(/\s+/g, '');

    const textContainsTerm = (text = '', term = '') => {
      if (!term) return false;

      const normalizedText = ` ${normalizeSingleCardSearchQuery(text)} `;
      const normalizedTerm = normalizeSingleCardSearchQuery(term);
      const compactText = compactSingleCardSearchQuery(text);
      const compactTerm = compactSingleCardSearchQuery(term);
      const pattern = escapeRegExp(normalizedTerm).replace(/\s+/g, '[\\s-]+');

      return new RegExp(`(^|[\\s,;/|(-])${pattern}(?=$|[\\s,;/|)-])`, 'i').test(normalizedText)
        || normalizedText.includes(` ${normalizedTerm} `)
        || (compactTerm && compactText.includes(compactTerm));
    };

    const textContainsAnyTerm = (text = '', terms = []) => terms.some((term) => textContainsTerm(text, term));

    const getChipByFilter = (filter) => chips.find((chip) => chip.dataset.singleFilter === filter);

    const getFilterChipsByKey = (filter) => chips.filter((candidate) => candidate.dataset.singleFilter === filter);

    const setActiveCardType = (filter = '') => {
      activeCardType = filter || '';
      chips.forEach((candidate) => {
        const isActive = Boolean(activeCardType) && getFilterChipsByKey(activeCardType).includes(candidate);
        candidate.classList.toggle('is-active', isActive);
        candidate.setAttribute('aria-pressed', String(isActive));
      });
    };

    const getNextCardType = (filter) => activeCardType === filter ? '' : filter;

    const getFilterMatchText = (product = {}, chip = null, sortData = null) => {
      const productData = sortData || getProductSortData(product);

      if (chip?.dataset.singleMatchSource === 'finish') {
        return [
          getProductMetafieldValue(product, 'finish'),
          getProductMetafieldValue(product, 'foil'),
          getProductMetafieldValue(product, 'version'),
        ].filter(Boolean).join(' ');
      }

      if (chip?.dataset.singleMatchSource === 'variant') {
        return [
          getProductMetafieldValue(product, 'variant_type'),
          getProductMetafieldValue(product, 'variant'),
          getProductMetafieldValue(product, 'card_variant'),
        ].filter(Boolean).join(' ');
      }

      return getProductSearchText(product, productData.parsed, productData.variant);
    };

    const matchesFilterChip = (product, chip, sortData = null) => {
      if (!chip) return true;

      const productData = sortData || getProductSortData(product);
      const combined = getFilterMatchText(product, chip, productData);
      const includeTerms = splitTerms(chip.dataset.singleTerms || chip.dataset.singleFilter);
      const excludeTerms = splitTerms(chip.dataset.singleExcludeTerms);

      if (textContainsAnyTerm(combined, excludeTerms)) return false;
      if (!includeTerms.length) return true;

      return textContainsAnyTerm(combined, includeTerms);
    };

    const matchesFilters = (product, sortData = null) => {
      if (activeIllustrators.size && !illustratorCore.matches(product, activeIllustrators)) return false;
      if (!activeCardType) return true;
      return matchesFilterChip(product, getChipByFilter(activeCardType), sortData);
    };

    const normalizeMetafieldValue = (value) => {
      if (value === null || typeof value === 'undefined') return '';
      if (Array.isArray(value)) return normalizeText(value.join(' '));
      if (typeof value === 'object') {
        if (typeof value.value !== 'undefined') return normalizeMetafieldValue(value.value);
        return normalizeText(Object.values(value).join(' '));
      }
      return normalizeText(String(value));
    };

    const getProductMetafieldValue = (product = {}, key = '') => (
      normalizeMetafieldValue(product.q_metafields?.[key] || product.metafields?.[key] || '')
    );

    const getFirstProductMetafieldValue = (product = {}, keys = []) => {
      for (const key of keys) {
        const value = getProductMetafieldValue(product, key);
        if (value) return value;
      }

      return '';
    };

    const getProductSearchText = (product = {}, parsed = {}, variant = {}) => {
      const metafieldValues = [
        getProductMetafieldValue(product, 'search'),
        getProductMetafieldValue(product, 'card_name'),
        getProductMetafieldValue(product, 'name'),
        getProductMetafieldValue(product, 'number'),
        getProductMetafieldValue(product, 'card_number'),
        getProductMetafieldValue(product, 'set_name'),
        getProductMetafieldValue(product, 'set'),
        getProductMetafieldValue(product, 'tcg_set'),
        getProductMetafieldValue(product, 'rarity'),
        getProductMetafieldValue(product, 'variant_type'),
        getProductMetafieldValue(product, 'variant'),
        getProductMetafieldValue(product, 'card_variant'),
        getProductMetafieldValue(product, 'finish'),
        getProductMetafieldValue(product, 'foil'),
        getProductMetafieldValue(product, 'language'),
        getProductMetafieldValue(product, 'version'),
        getProductMetafieldValue(product, 'condition'),
      ];

      return normalizeSearchText([
        product.title,
        product.handle,
        product.type,
        product.product_type,
        parsed.name,
        parsed.number,
        parsed.set,
        parsed.variant,
        variant?.title,
        variant?.name,
        ...metafieldValues,
        ...Object.values(product.q_metafields || {}),
        ...Object.values(product.metafields || {}),
        ...getProductTags(product),
        ...getVariantTitles(product),
      ].filter(Boolean).join(' '));
    };

    const matchesProductSearch = (product = {}) => catalog.matchesQuery(product, preparedProductQuery);

    const normalizeProductSort = (value) => {
      const fallback = 'price-desc';
      const normalized = String(value || '').toLowerCase();
      const allowed = new Set([
        'collection-default',
        'number-asc',
        'number-desc',
        'price-asc',
        'price-desc',
        'rarity-asc',
        'rarity-desc',
      ]);
      return allowed.has(normalized) ? normalized : fallback;
    };

    const parseSortableNumberFromText = (value = '') => {
      const normalized = normalizeText(value);
      if (!normalized) return Number.POSITIVE_INFINITY;

      const slashMatch = normalized.match(/\b(?:[A-Za-z]{0,6}\s*)?(\d{1,6})(?:\s*\/\s*(?:[A-Za-z]{0,6}\s*)?(\d{1,6}))?\b/i);
      if (slashMatch && slashMatch[1]) {
        const parsed = Number(slashMatch[1]);
        if (Number.isFinite(parsed)) return parsed;
      }

      const firstNumber = normalized.match(/\d+([\.,]\d+)?/);
      if (!firstNumber) return Number.POSITIVE_INFINITY;

      const numeric = Number(firstNumber[0].replace(',', '.'));
      return Number.isFinite(numeric) ? numeric : Number.POSITIVE_INFINITY;
    };

    const getSortableNumberValue = (product, parsed = {}, variant = {}) => {
      const numberCandidates = [
        getFirstProductMetafieldValue(product, ['card_number', 'number', 'collector_number']),
        getProductCardValue(product, 'number', parsed, variant),
        variant?.title,
        variant?.name,
        parsed.number,
        parsed.name,
        product.title,
        product.handle,
      ];

      for (const candidate of numberCandidates) {
        const parsedValue = parseSortableNumberFromText(candidate);
        if (Number.isFinite(parsedValue) && parsedValue !== Number.POSITIVE_INFINITY) {
          return parsedValue;
        }
      }

      return Number.POSITIVE_INFINITY;
    };

    const getSortablePriceValue = (product, _parsed = {}, variant = {}) => {
      const candidates = [];
      if (variant?.price !== undefined) candidates.push(variant.price);
      if (product.price_min !== undefined) candidates.push(product.price_min);
      if (product.price !== undefined) candidates.push(product.price);

      const variants = Array.isArray(product.variants) ? product.variants : [];
      candidates.push(...variants.map((item) => item?.price).filter((item) => typeof item !== 'undefined'));

      for (const candidate of candidates) {
        const cents = normalizePriceToCents(candidate);
        if (Number.isFinite(cents)) return cents;
      }

      return Number.POSITIVE_INFINITY;
    };

    const getSortableRarityValue = (product, parsed = {}, variant = {}) => (
      getProductMetafieldValue(product, 'rarity') || normalizeText(getProductCardValue(product, 'rarity', parsed, variant))
    );

    const getProductSortData = (product = {}, originalIndex = -1) => {
      const cached = sortCache.get(product);
      if (cached) return cached;

      const parsed = parseTcgCardTitle(product, getProductParseConfig());
      const variant = getPrimaryVariant(product);
      const index = Number.isFinite(Number(originalIndex)) && originalIndex >= 0 ? Number(originalIndex) : 0;
      const sortData = {
        product,
        parsed,
        variant,
        number: getSortableNumberValue(product, parsed, variant),
        price: getSortablePriceValue(product, parsed, variant),
        rarity: getSortableRarityValue(product, parsed, variant),
        originalIndex: index,
        title: normalizeText(parsed.name || product.title || ''),
      };

      sortCache.set(product, sortData);
      return sortData;
    };

    const getSortValueA = (sortKey, data) => {
      if (sortKey === 'price-asc' || sortKey === 'price-desc') return data.price;
      if (sortKey === 'rarity-asc' || sortKey === 'rarity-desc') return data.rarity;
      return data.number;
    };

    const compareProductSortValues = (left, right) => {
      if (currentSort === 'collection-default') {
        return (left.sortData.originalIndex || 0) - (right.sortData.originalIndex || 0);
      }

      const isNumberSort = currentSort.startsWith('number');
      const isPriceSort = currentSort.startsWith('price');
      const isRaritySort = currentSort.startsWith('rarity');
      const direction = currentSort.endsWith('desc') ? -1 : 1;
      const leftData = left.sortData;
      const rightData = right.sortData;
      const leftValue = getSortValueA(currentSort, leftData);
      const rightValue = getSortValueA(currentSort, rightData);

      if (isNumberSort || isPriceSort) {
        const leftMissing = !Number.isFinite(leftValue);
        const rightMissing = !Number.isFinite(rightValue);
        if (leftMissing && rightMissing) {
          const titleSort = leftData.title.localeCompare(rightData.title, document.documentElement.lang || 'en', { numeric: true, sensitivity: 'base' });
          return titleSort;
        }
        if (leftMissing !== rightMissing) return leftMissing ? 1 : -1;
        return direction * (leftValue - rightValue);
      }

      if (isRaritySort) {
        const leftMissing = !leftValue;
        const rightMissing = !rightValue;
        if (leftMissing && rightMissing) {
          const numberSort = leftData.number - rightData.number;
          if (Number.isFinite(numberSort) && numberSort !== 0) return numberSort;
          return leftData.title.localeCompare(rightData.title, document.documentElement.lang || 'en', { numeric: true, sensitivity: 'base' });
        }
        if (leftMissing !== rightMissing) return leftMissing ? 1 : -1;
        const textSort = leftValue.localeCompare(rightValue, document.documentElement.lang || 'en', { sensitivity: 'base' });
        if (textSort !== 0) return direction * textSort;
      }

      const numberSort = leftData.number - rightData.number;
      if (numberSort !== 0 && Number.isFinite(numberSort)) return direction * numberSort;

      return leftData.title.localeCompare(rightData.title, document.documentElement.lang || 'en', { numeric: true, sensitivity: 'base' });
    };

    const getFilteredSortedProducts = () => {
      const entries = allProducts.map((product, index) => ({
        sortData: getProductSortData(product, index),
        product,
      })).filter(({ product, sortData }) => (
        matchesAvailabilityFilter(product, showSoldOutProducts)
        && matchesFilters(product, sortData)
        && matchesProductSearch(product, sortData)
      ));

      entries.sort(compareProductSortValues);
      return entries.map(({ product }) => product);
    };

    const resetLoadMoreFooter = () => {
      if (productsFooter) productsFooter.hidden = true;
      if (progressTarget) progressTarget.textContent = '';
      if (loadMoreButton) {
        loadMoreButton.hidden = true;
        loadMoreButton.disabled = false;
        loadMoreButton.textContent = labels.loadMore;
      }
    };

    const getKnownCollectionTotal = (handle = activeHandle) => {
      if (handle === activeHandle && activeTotalCount > 0) return activeTotalCount;
      if (handle === root.dataset.baseHandle) return Number(root.dataset.baseTotalCount || 0) || 0;
      return 0;
    };

    const loadNextProductDataPage = () => {
      const requestToken = activeCollectionRequestToken;
      if (productDataLoadPromise && productDataLoadPromiseToken === requestToken) return productDataLoadPromise;
      if (!activeHandle || !productDataHasMore || !productDataSourceReliable) return Promise.resolve({ loaded: false, failed: false });

      const handle = activeHandle;
      const limit = Number(root.dataset.productLimit || 248);
      const knownTotal = getKnownCollectionTotal(handle);
      productDataIsLoadingMore = true;

      const loadPromise = (async () => {
        try {
          const { products: nextProducts, pagesLoaded, reachedEnd: windowReachedEnd } = await fetchCollectionProductsWindow(handle, {
            page: productDataPage + 1,
            limit,
            knownTotal,
          });
          if (handle !== activeHandle || activeCollectionRequestToken !== requestToken) return { loaded: false, failed: false };

          const previousLength = allProducts.length;
          allProducts = mergeUniqueProducts(allProducts, nextProducts);
          const hasGrowth = allProducts.length > previousLength;
          const reachedEnd = windowReachedEnd;
          productDataPage += pagesLoaded;

          if (!hasGrowth) {
            productDataHasMore = false;
            activeKnownComplete = reachedEnd;
            if (reachedEnd && activeTotalCount < allProducts.length) {
              activeTotalCount = allProducts.length;
            }
            if (!reachedEnd) productDataSourceReliable = false;

            return { loaded: false, failed: false };
          }

          productDataHasMore = !reachedEnd;
          activeKnownComplete = reachedEnd;
          if (activeKnownComplete && activeTotalCount < allProducts.length) {
            activeTotalCount = allProducts.length;
          }
          sortCache = new WeakMap();

          return { loaded: allProducts.length > previousLength, failed: false };
        } catch {
          return { loaded: false, failed: true };
        } finally {
          if (productDataLoadPromise === loadPromise) {
            productDataLoadPromise = null;
            productDataLoadPromiseToken = 0;
            productDataIsLoadingMore = false;
          }
        }
      })();

      productDataLoadPromise = loadPromise;
      productDataLoadPromiseToken = requestToken;
      return loadPromise;
    };

    const getActiveTotalCount = () => {
      if (activeTotalCount > 0) return activeTotalCount;
      if (activeKnownComplete) return allProducts.length;
      return Math.max(allProducts.length, visibleProductCount);
    };

    const formatVisibleProductProgress = (visible, total, hits = false) => {
      const template = hits ? labels.visibleHits : labels.visibleProducts;
      return template
        .replace('[visible]', String(Math.max(0, visible)))
        .replace('[total]', String(Math.max(0, total)));
    };

    const hasActiveProductRefinement = () => Boolean(
      normalizeSingleCardSearchQuery(currentProductSearchTerm)
      || activeCardType
      || activeIllustrators.size
      || !showSoldOutProducts
    );

    const setLoadMoreLoading = (loading) => {
      if (!loadMoreButton) return;
      loadMoreButton.disabled = loading;
      loadMoreButton.textContent = loading ? labels.loadMoreLoading : labels.loadMore;
    };

    const renderLoadMoreControl = (visibleCount, totalCount, canLoadMore, hits = false) => {
      if (!productsFooter || !progressTarget || !loadMoreButton) return;

      const safeVisible = Math.min(Math.max(0, visibleCount), Math.max(0, totalCount));
      const safeTotal = Math.max(0, totalCount);
      productsFooter.hidden = safeTotal <= 0;
      progressTarget.textContent = formatVisibleProductProgress(safeVisible, safeTotal, hits);

      if (!canLoadMore) {
        loadMoreButton.hidden = true;
        loadMoreButton.disabled = true;
        loadMoreButton.textContent = labels.loadMoreComplete;
        return;
      }

      loadMoreButton.hidden = false;
      loadMoreButton.disabled = productDataIsLoadingMore;
      loadMoreButton.textContent = productDataIsLoadingMore ? labels.loadMoreLoading : labels.loadMore;
    };

    const requestNextProductBatch = async () => {
      const requestHandle = activeHandle;
      const requestToken = activeCollectionRequestToken;
      if (!requestHandle) return;

      setLoadMoreLoading(true);
      try {
        if (activeHandle !== requestHandle || activeCollectionRequestToken !== requestToken) return;

        const entries = getFilteredSortedProducts();
        if (visibleProductCount < entries.length) {
          visibleProductCount = Math.min(visibleProductCount + settings.productPageSize, entries.length);
          if (activeHandle !== requestHandle || activeCollectionRequestToken !== requestToken) return;
          renderProducts();
          return;
        }

        const previousVisibleCount = visibleProductCount;
        const { loaded } = await loadNextProductDataPage();
        if (activeHandle !== requestHandle || activeCollectionRequestToken !== requestToken) return;
        if (!loaded) {
          renderProducts();
          return;
        }

        const nextEntries = getFilteredSortedProducts();
        visibleProductCount = Math.min(previousVisibleCount + settings.productPageSize, nextEntries.length);
        renderProducts();
      } finally {
        if (activeHandle === requestHandle && activeCollectionRequestToken === requestToken) setLoadMoreLoading(false);
      }
    };

    const clearPendingProductRender = () => {
      productRenderPending = false;
      if (productRenderFlushFrame === null) return;
      window.cancelAnimationFrame(productRenderFlushFrame);
      productRenderFlushFrame = null;
    };

    const schedulePendingProductRender = () => {
      if (!productRenderPending || activeProductIndex !== null || productRenderFlushFrame !== null) return;

      productRenderFlushFrame = window.requestAnimationFrame(() => {
        productRenderFlushFrame = null;
        if (!productRenderPending || activeProductIndex !== null) return;
        renderProducts();
      });
    };

    const requestProductRenderAfterHydration = () => {
      productRenderPending = true;
      schedulePendingProductRender();
    };

    const ensureActiveCollectionDataForFilters = async (requestId) => {
      const requestHandle = activeHandle;
      const requestToken = activeCollectionRequestToken;
      let hasLoadedProducts = false;
      while (
        requestId === searchDataLoadRequestId
        && activeHandle
        && hasActiveProductRefinement()
        && productDataHasMore
        && productDataSourceReliable
      ) {
        const loadResult = await loadNextProductDataPage();
        const hasLoadedMore = loadResult.loaded;
        if (activeHandle !== requestHandle || activeCollectionRequestToken !== requestToken) return;
        if (requestId !== searchDataLoadRequestId) {
          if (hasLoadedMore) requestProductRenderAfterHydration();
          return;
        }
        if (!hasLoadedMore) break;
        hasLoadedProducts = true;
      }

      if (
        hasLoadedProducts
        && requestId === searchDataLoadRequestId
        && activeHandle === requestHandle
        && activeCollectionRequestToken === requestToken
      ) {
        requestProductRenderAfterHydration();
      }
    };

    const scheduleProductSearchDataLoad = () => {
      window.clearTimeout(searchDataLoadTimer);
      searchDataLoadRequestId += 1;

      if (!activeHandle || !hasActiveProductRefinement() || !productDataHasMore) return;

      const requestId = searchDataLoadRequestId;
      searchDataLoadTimer = window.setTimeout(() => {
        ensureActiveCollectionDataForFilters(requestId);
      }, 220);
    };

    const ensureSortState = () => {
      currentSort = normalizeProductSort(currentSort);
      if (sortSelect && sortSelect.value !== currentSort) {
        sortSelect.value = currentSort;
      }
    };

    currentSort = normalizeProductSort(currentSort);
    ensureSortState();

    const setSingleLiveSearchState = (state, message = '') => {
      if (!liveSearch) return;

      liveSearch.hidden = false;
      liveSearch.classList.toggle('is-loading', state === 'loading');
      liveSearch.classList.toggle('is-empty', state === 'empty');
      liveSearch.classList.toggle('is-error', state === 'error');
      if (liveSearchStatus) liveSearchStatus.textContent = message;
    };

    const hideSingleLiveSearch = () => {
      window.clearTimeout(liveSearchTimer);
      liveSearchRequestId += 1;
      if (liveSearch) liveSearch.hidden = true;
      if (liveSearchResults) liveSearchResults.innerHTML = '';
      if (liveSearchStatus) liveSearchStatus.textContent = '';
      if (liveSearchMore) liveSearchMore.hidden = true;
    };

    const getProductImageSource = (product) => {
      if (typeof product.image === 'string') return product.image;
      if (product.image?.src) return product.image.src;
      if (product.image?.url) return product.image.url;
      if (typeof product.featured_image === 'string') return product.featured_image;
      if (product.featured_image?.src) return product.featured_image.src;
      if (product.featured_image?.url) return product.featured_image.url;
      if (product.images?.[0]?.src) return product.images[0].src;
      if (product.images?.[0]?.url) return product.images[0].url;
      if (product.variants?.[0]?.featured_image?.url) return product.variants[0].featured_image.url;
      if (product.variants?.[0]?.featured_image?.src) return product.variants[0].featured_image.src;
      if (typeof product.variants?.[0]?.image === 'string') return product.variants[0].image;
      if (product.variants?.[0]?.image?.src) return product.variants[0].image.src;
      if (product.variants?.[0]?.image?.url) return product.variants[0].image.url;
      return '';
    };

    const getOptimizedShopifyImageUrl = (source, width = 360) => {
      if (!source) return '';

      const targetWidth = Math.max(80, Math.min(1200, Math.round(Number(width) || 360)));

      try {
        const parsed = new URL(source, window.location.origin);
        const host = parsed.hostname.toLowerCase();
        const canResize = host.includes('shopify') || host.includes('3ddruckschmidt.de') || parsed.pathname.includes('/cdn/shop/');

        if (!canResize) return source;

        parsed.searchParams.set('width', String(targetWidth));
        return parsed.href;
      } catch {
        const separator = source.includes('?') ? '&' : '?';
        return `${source}${separator}width=${targetWidth}`;
      }
    };

    const getProductImageSrcset = (source, widths = []) => widths
      .map((width) => `${getOptimizedShopifyImageUrl(source, width)} ${width}w`)
      .join(', ');

    const getProductCardImage = (product) => {
      const source = getProductImageSource(product);

      if (!source) {
        return { src: '', srcset: '' };
      }

      return {
        src: getOptimizedShopifyImageUrl(source, 480),
        srcset: getProductImageSrcset(source, [180, 320, 480, 720]),
      };
    };

    const getPredictiveProductImage = (product) => getOptimizedShopifyImageUrl(getProductImageSource(product), 180);

    const renderTcgVariantBadge = (variant, variantLabel) => {
      const labelsByVariant = {
        holo: 'Holo',
        reverse_holo: 'Reverse Holo',
        rocket: 'Team Rocket',
        stamp: 'Stamp',
        pokeball: 'Pokéball',
        master_ball: 'Master Ball',
        normal: 'Normal',
      };
      const iconUrl = liveSearchIcons[variant];
      const label = variantLabel || labelsByVariant[variant] || labelsByVariant.normal;

      if (iconUrl) {
        return `
          <span class="q-single-live-search__variant" aria-label="${escapeHtml(label)}" title="${escapeHtml(label)}">
            <img src="${escapeHtml(iconUrl)}" alt="" loading="lazy">
            <span class="q-single-live-search__variant-text">${escapeHtml(label)}</span>
          </span>
        `;
      }

      return `
        <span class="q-single-live-search__variant q-single-live-search__variant--normal" aria-label="${escapeHtml(label)}" title="${escapeHtml(label)}">
          <span class="q-single-live-search__variant-dot" aria-hidden="true"></span>
          <span class="q-single-live-search__variant-text">${escapeHtml(label)}</span>
        </span>
      `;
    };

    const getLiveSearchVariantLabel = (product = {}, parsed = {}, variant = {}) => {
      const displayVariant = getDisplayVariant(product, parsed, variant);
      return displayVariant ? String(displayVariant) : '';
    };

    const hasAvailableLiveSearchVariant = (product = {}) => (
      Array.isArray(product.variants) && product.variants.some(isVariantAvailable)
    );

    const prioritizeLiveSearchProducts = (products = []) => {
      const availableProducts = [];
      const unavailableProducts = [];
      products.forEach((product) => {
        (hasAvailableLiveSearchVariant(product) ? availableProducts : unavailableProducts).push(product);
      });
      return [...availableProducts, ...unavailableProducts];
    };

    const renderSingleLiveSearchRow = (product) => {
        const parsed = parseTcgCardTitle(product, getProductParseConfig());
        const variant = getPrimaryVariant(product);
        const image = getPredictiveProductImage(product);
        const productUrl = product.url || `${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(product.handle || '')}`;
        const soldOut = isProductSoldOut(product);
        const canAddToCart = Boolean(variant?.id) && isVariantAvailable(variant);
        const unavailableLabel = soldOut ? 'Ausverkauft' : 'Nicht verfügbar';
        const price = getProductDisplayPrice(product);
        const priceLabel = soldOut ? 'Ausverkauft' : (price || '-');
        const cardName = getProductCardValue(product, 'parsed_name', parsed, variant);
        const cartLabel = `${settings.productCardBuyLabel}: ${cardName}`;
        const cardNumber = getProductCardValue(product, 'number', parsed, variant);
        const setName = getProductCardValue(product, 'set', parsed, variant);
        const variantKey = getVariantKeyFromValue(getProductFoilMetafieldValue(product), parsed.variant);
        const variantLabel = getLiveSearchVariantLabel(product, parsed, variant);

        return `
          <div class="q-single-live-search__row" data-catalog-product-id="${escapeHtml(product.id)}">
            <a class="q-single-live-search__link" href="${escapeHtml(productUrl)}">
              <span class="q-single-live-search__image">
                ${image ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(cardName)}" loading="lazy">` : '<span class="q-single-live-search__placeholder" aria-hidden="true"></span>'}
              </span>
              <span class="q-single-live-search__name">${escapeHtml(cardName)}</span>
              <span class="q-single-live-search__number">${escapeHtml(cardNumber || '-')}</span>
              <span class="q-single-live-search__set">${escapeHtml(setName || '-')}</span>
              ${renderTcgVariantBadge(variantKey, variantLabel)}
              <span class="q-single-live-search__price${soldOut ? ' q-single-live-search__price--sold-out' : ''}">${escapeHtml(priceLabel)}</span>
            </a>
            ${canAddToCart ? `
              <form class="q-single-live-search__buy-form" action="${window.Shopify?.routes?.root || '/'}cart/add" method="post" data-product-form data-catalog-product-handle="${escapeHtml(product.handle)}">
                <input type="hidden" name="id" value="${escapeHtml(variant.id)}">
                <input type="hidden" name="quantity" value="1">
                <button class="q-single-live-search__buy" type="submit" name="add" aria-label="${escapeHtml(cartLabel)}" title="${escapeHtml(settings.productCardBuyLabel)}" data-add-to-cart-text="${escapeHtml(settings.productCardBuyLabel)}" data-add-to-cart-loading="Wird hinzugefügt">
                  ${singleCardActionIcons.cart}
                  <span class="visually-hidden">${escapeHtml(cartLabel)}</span>
                </button>
              </form>
            ` : `
              <button class="q-single-live-search__buy is-disabled" type="button" disabled aria-label="${escapeHtml(`${unavailableLabel}: ${cardName}`)}" title="${escapeHtml(unavailableLabel)}">
                ${singleCardActionIcons.cart}
                <span class="visually-hidden">${escapeHtml(unavailableLabel)}</span>
              </button>
            `}
          </div>
        `;

    };

    const renderSingleLiveSearchResults = (results, query) => {
      if (!liveSearch || !liveSearchResults) return;
      const visibleResults = results;

      if (!visibleResults.length) {
        setSingleLiveSearchState('empty', '');
        liveSearchResults.innerHTML = `<div class="q-single-live-search__empty">${escapeHtml(labels.liveEmpty)}</div>`;
        if (liveSearchMore) liveSearchMore.hidden = true;
        return;
      }

      setSingleLiveSearchState('ready', `${visibleResults.length} ${labels.liveCount}`);
      if (liveSearchMore) {
        liveSearchMore.textContent = labels.liveMore;
        liveSearchMore.href = `${window.Shopify?.routes?.root || '/'}search?type=product&q=${encodeURIComponent(query)}`;
        liveSearchMore.hidden = false;
      }

      liveSearchResults.innerHTML = visibleResults.map(renderSingleLiveSearchRow).join('');
      if (catalogWasRestored) visibleResults.filter(isProductSoldOut).forEach((product) => refreshCatalogProduct(product.handle));
    };

    const getLiveSearchResultProducts = (sourceProducts = [], term = '') => {
      const preparedQuery = catalog.prepareQuery(term);
      const matchingProducts = sourceProducts.filter((product) => catalog.matchesQuery(product, preparedQuery))
      .filter((product) => !activeIllustrators.size || matchesFilters(product))
      .filter((product) => passesSingleLiveSearchRules(
        product,
        settings.liveSearchWhitelistTerms,
        settings.liveSearchBlacklistTerms,
      ));

      return prioritizeLiveSearchProducts(matchingProducts).slice(0, settings.liveSearchLimit);
    };

    const fetchSingleLiveSearch = async (query, requestId) => {

      if (requestId !== liveSearchRequestId) return [];

      const term = normalizeSingleCardSearchQuery(query);
      const sourceProducts = await getLiveSearchProducts();
      if (requestId !== liveSearchRequestId) return [];

      return getLiveSearchResultProducts(sourceProducts, term);
    };

    const scheduleSingleLiveSearch = (query) => {
      if (!settings.liveSearchEnabled || !liveSearch) return;

      window.clearTimeout(liveSearchTimer);
      const term = normalizeText(query);

      if (term.length < 2) {
        hideSingleLiveSearch();
        return;
      }

      liveSearchRequestId += 1;
      const requestId = liveSearchRequestId;
      setSingleLiveSearchState('loading', labels.liveLoading);
      if (liveSearchResults) liveSearchResults.innerHTML = '';
      if (liveSearchMore) liveSearchMore.hidden = true;

      liveSearchTimer = window.setTimeout(async () => {
        try {
          const results = await fetchSingleLiveSearch(term, requestId);
          if (requestId === liveSearchRequestId) renderSingleLiveSearchResults(results, term);
        } catch (error) {
          if (error.name === 'AbortError') return;
          if (requestId !== liveSearchRequestId) return;
          setSingleLiveSearchState('error', '');
          if (liveSearchResults) liveSearchResults.innerHTML = `<div class="q-single-live-search__empty">${escapeHtml(labels.liveError)}</div>`;
        }
      }, 180);
    };

    const renderSkeletons = (count = 8) => {
      if (!products) return;

      resetLoadMoreFooter();
      closeExpandedProduct?.(true);
      productCloseTimers.forEach(clearTimerEntry);
      productCloseTimers.clear();
      panel?.classList.remove('is-product-expanded');
      products.classList.remove('is-switching-next', 'is-switching-prev');
      products.innerHTML = Array.from({ length: Math.min(count, 12) }, () => `
        <div class="q-single-skeleton">
          <div class="q-single-skeleton__img"></div>
          <div class="q-single-skeleton__line"></div>
          <div class="q-single-skeleton__line q-single-skeleton__line--short"></div>
        </div>
      `).join('');
    };

    const getProductParseConfig = () => ({
      hiddenTerms: [],
      useTags: false,
      reverseTerms: [],
      holoTerms: [],
      normalTerms: [],
      languageTerms: [],
      conditionTerms: [],
      rocketTerms: [],
      pokeballTerms: [],
      stampTerms: [],
      masterballTerms: [],
      setTagPrefixes: [],
      numberTagPrefixes: [],
      tagSetFallbackEnabled: false,
      nonSetTagTerms: [],
      setCodeEnabled: false,
    });

    const isVariantAvailable = (variant = {}) => variant?.available === true || variant?.available === 'true';

    const isProductSoldOut = (product = {}) => {
      const variants = Array.isArray(product.variants) ? product.variants : [];
      return variants.length > 0 && !variants.some((variant) => isVariantAvailable(variant));
    };

    const matchesAvailabilityFilter = (product = {}, showSoldOut = false) => showSoldOut || !isProductSoldOut(product);

    const getPrimaryVariant = (product = {}) => {
      if (!Array.isArray(product.variants) || !product.variants.length) return null;
      return product.variants.find((variant) => isVariantAvailable(variant)) || product.variants[0];
    };

    const getProductDisplayPrice = (product = {}, sortData = null) => {
      if (isProductSoldOut(product)) return '';
      if (sortData && Number.isFinite(sortData.price)) return formatMoney(sortData.price);

      const variant = getPrimaryVariant(product);
      const priceValue = variant?.price || product.price_min || product.price || product.variants?.[0]?.price;
      return priceValue ? formatMoney(normalizePriceToCents(priceValue)) : '';
    };

    const getProductFoilMetafieldValue = (product = {}) => getFirstProductMetafieldValue(product, [
      'version',
      'finish',
      'foil',
      'holo',
    ]) || getTagValueByPrefixes(getProductTags(product), ['Version:', 'Version=', 'Foil:', 'Foil=', 'Finish:', 'Finish=']);

    const getProductVariantTypeMetafieldValue = (product = {}) => getFirstProductMetafieldValue(product, [
      'variant_type',
      'variant',
      'card_variant',
    ]);

    const getVariantKeyFromValue = (value = '', fallback = 'normal') => {
      const normalized = normalizeSearchText(value);
      if (!normalized) return fallback || 'normal';
      if (normalized.includes('reverse')) return 'reverse_holo';
      if (normalized.includes('master') || normalized.includes('meister')) return 'master_ball';
      if (normalized.includes('rocket')) return 'rocket';
      if (normalized.includes('poke') || normalized.includes('poké')) return 'pokeball';
      if (normalized.includes('stamp') || normalized.includes('stempel')) return 'stamp';
      if (normalized.includes('holo')) return 'holo';
      if (normalized.includes('normal')) return 'normal';
      return fallback || 'normal';
    };

    const getDisplayVariant = (product = {}, parsed = {}, variant = {}) => {
      const metafieldVariant = getProductVariantTypeMetafieldValue(product);
      if (metafieldVariant) return metafieldVariant;

      const variantTitle = normalizeText(variant?.title || '');
      if (variantTitle && !/^default title$/i.test(variantTitle)) return variantTitle;

      const labelsByVariant = {
        holo: 'Holo',
        reverse_holo: 'Reverse Holo',
        rocket: 'Team Rocket',
        stamp: 'Stamp',
        pokeball: 'Pokeball',
        master_ball: 'Master Ball',
      };

      return labelsByVariant[parsed.variant] || '';
    };

    const getProductCardValue = (product = {}, source = 'none', parsed = {}, variant = {}) => {
      if (!source || source === 'none') return '';

      const tags = getProductTags(product);
      const rarity = getTagValueByPrefixes(tags, ['Rarity:', 'Rarity=', 'Seltenheit:', 'Seltenheit=', 'Rare:', 'Rare=']);
      const version = getTagValueByPrefixes(tags, ['Version:', 'Version=', 'Foil:', 'Foil=', 'Finish:', 'Finish=']);
      const productType = product.product_type || product.type || '';

      switch (source) {
        case 'title':
          return product.title || '';
        case 'parsed_name':
          return getProductMetafieldValue(product, 'card_name') || getProductMetafieldValue(product, 'name') || parsed.name || product.title || '';
        case 'number':
          return getFirstProductMetafieldValue(product, ['number', 'card_number']) || parsed.number || '';
        case 'set':
          return getFirstProductMetafieldValue(product, ['set_name', 'set', 'tcg_set']) || parsed.set || productType || '';
        case 'variant':
          return getDisplayVariant(product, parsed, variant);
        case 'rarity':
          return getProductMetafieldValue(product, 'rarity') || rarity;
        case 'version':
          return getProductFoilMetafieldValue(product) || version;
        case 'product_type':
          return productType;
        case 'first_tag':
          return tags[0] || '';
        default:
          return '';
      }
    };

    const prefersReducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    const getProductCardByIndex = (index) => products?.querySelector(`[data-product-index="${index}"]`);

    const clearTimerEntry = (timer) => {
      if (Array.isArray(timer)) {
        timer.forEach((entry) => window.clearTimeout(entry));
        return;
      }
      window.clearTimeout(timer);
    };

    const clearProductCloseTimer = (index) => {
      const timer = productCloseTimers.get(index);
      if (!timer) return;
      clearTimerEntry(timer);
      productCloseTimers.delete(index);
    };

    const setProductCardDetailsHidden = (card, hidden) => {
      card?.querySelector('.q-single-product__info')?.toggleAttribute('hidden', hidden);
      card?.querySelector('[data-single-product-close]')?.toggleAttribute('hidden', hidden);
    };

    const clearExpandedProductSpace = () => {
      products?.style.removeProperty('--q-single-product-expanded-space');
    };

    const clearProductsEndSpace = () => {
      products?.classList.remove('has-product-items');
      products?.style.removeProperty('--q-single-products-end-space');
    };

    const updateProductsEndSpace = () => {
      if (!products) return;
      if (activeProductIndex === null) {
        clearProductsEndSpace();
        return;
      }

      const activeCard = getProductCardByIndex(activeProductIndex) || products.querySelector('.q-single-product.is-expanded');
      if (!activeCard) {
        clearProductsEndSpace();
        return;
      }

      const cardRect = activeCard.getBoundingClientRect();
      const styles = window.getComputedStyle(products);
      const rowGap = Number.parseFloat(styles.rowGap || styles.gap) || 0;
      const cardSize = Math.max(cardRect.width, cardRect.height);
      const endSpace = Math.ceil(cardSize + rowGap);

      products.style.setProperty('--q-single-products-end-space', `${Math.max(0, endSpace)}px`);
      products.classList.add('has-product-items');
    };

    const clearProductReserveHeight = () => {
      products?.classList.remove('has-product-reserve');
      products?.style.removeProperty('--q-single-products-reserve-height');
    };

    const usesProductOverlay = () => !prefersReducedMotion();

    const setProductExpandedOffset = (card) => {
      if (!products || !card || usesProductOverlay()) {
        card?.style.removeProperty('--q-single-product-expanded-offset-x');
        return;
      }

      const productsRect = products.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      const productsStyle = window.getComputedStyle(products);
      const paddingLeft = Number.parseFloat(productsStyle.paddingLeft) || 0;
      const paddingRight = Number.parseFloat(productsStyle.paddingRight) || 0;
      const contentLeft = productsRect.left + paddingLeft;
      const contentRight = productsRect.right - paddingRight;
      const contentWidth = Math.max(0, contentRight - contentLeft);
      if (!contentWidth || !cardRect.width) {
        card.style.removeProperty('--q-single-product-expanded-offset-x');
        return;
      }

      const scale = Number.parseFloat(window.getComputedStyle(card).getPropertyValue('--q-single-product-expanded-scale')) || 1.42;
      const expandedHalfWidth = (cardRect.width * scale) / 2;
      const targetCenter = contentLeft + contentWidth / 2;
      const cardCenter = cardRect.left + cardRect.width / 2;
      const safeMinCenter = contentLeft + expandedHalfWidth + 6;
      const safeMaxCenter = contentRight - expandedHalfWidth - 6;
      const centeredTarget = safeMinCenter <= safeMaxCenter
        ? Math.min(Math.max(targetCenter, safeMinCenter), safeMaxCenter)
        : targetCenter;
      const offset = centeredTarget - cardCenter;

      if (Math.abs(offset) < 0.5) {
        card.style.removeProperty('--q-single-product-expanded-offset-x');
        return;
      }

      card.style.setProperty('--q-single-product-expanded-offset-x', `${Math.round(offset * 10) / 10}px`);
    };

    const updateProductsReserveHeight = () => {
      if (!products) return;

      const cards = products.querySelectorAll('[data-single-product-card]');
      if (!cards.length || !window.matchMedia?.('(max-width: 520px)').matches) {
        clearProductReserveHeight();
        return;
      }

      const styles = window.getComputedStyle(products);
      const paddingLeft = Number.parseFloat(styles.paddingLeft) || 0;
      const paddingRight = Number.parseFloat(styles.paddingRight) || 0;
      const expandedWidth = Math.max(0, products.clientWidth - paddingLeft - paddingRight - 12);
      const infoReserve = Math.max(168, Math.min(196, window.innerWidth * 0.44));
      const reserveHeight = Math.ceil(expandedWidth + infoReserve);

      products.style.setProperty('--q-single-products-reserve-height', `${reserveHeight}px`);
      products.classList.add('has-product-reserve');
    };

    const getProductPlaceholder = (card) => {
      const placeholder = card?.previousElementSibling;
      return placeholder?.classList.contains('q-single-product-placeholder') ? placeholder : null;
    };

    const removeProductPlaceholder = (card) => {
      getProductPlaceholder(card)?.remove();
    };

    const clearProductOverlay = (card) => {
      if (!card) return;

      const hadOverlay = card.classList.contains('is-overlay-active');
      card.classList.remove('is-overlay-active');
      card.style.removeProperty('--q-single-card-overlay-top');
      card.style.removeProperty('--q-single-card-overlay-left');
      card.style.removeProperty('--q-single-card-overlay-width');
      card.style.removeProperty('--q-single-card-overlay-close-transform');
      card.style.removeProperty('--q-single-product-expanded-offset-x');
      if (hadOverlay) {
        card.style.transition = 'none';
      }
      card.style.removeProperty('transform');
      card.style.removeProperty('transform-origin');
      if (hadOverlay) {
        window.requestAnimationFrame(() => card.style.removeProperty('transition'));
      } else {
        card.style.removeProperty('transition');
      }
      removeProductPlaceholder(card);
      products?.classList.remove('is-product-overlaying');
    };

    const prepareProductOverlay = (card) => {
      if (!products || !card || !usesProductOverlay()) return false;

      updateProductsReserveHeight();
      removeProductPlaceholder(card);

      const productsRect = products.getBoundingClientRect();
      const startRect = card.getBoundingClientRect();
      const productsStyle = window.getComputedStyle(products);
      const paddingLeft = Number.parseFloat(productsStyle.paddingLeft) || 0;
      const paddingRight = Number.parseFloat(productsStyle.paddingRight) || 0;
      const contentWidth = Math.max(0, products.clientWidth - paddingLeft - paddingRight);
      const mobileOverlay = window.matchMedia?.('(max-width: 520px)').matches;
      const targetWidth = mobileOverlay
        ? Math.max(0, contentWidth - 12)
        : Math.max(startRect.width, Math.min(contentWidth - 12, startRect.width * 1.42, 460));
      const targetLeft = paddingLeft + Math.max(6, (contentWidth - targetWidth) / 2);
      const startLeft = startRect.left - productsRect.left;
      const startTop = startRect.top - productsRect.top;
      const targetTop = Math.max(0, startTop);
      const startCenterX = startLeft + startRect.width / 2;
      const startCenterY = startTop + startRect.height / 2;
      const targetCenterX = targetLeft + targetWidth / 2;
      const targetCenterY = targetTop + targetWidth / 2;
      const scaleX = targetWidth ? startRect.width / targetWidth : 1;
      const scaleY = targetWidth ? startRect.height / targetWidth : 1;

      const placeholder = document.createElement('span');
      placeholder.className = 'q-single-product-placeholder';
      placeholder.setAttribute('aria-hidden', 'true');
      placeholder.style.minHeight = `${Math.ceil(startRect.height)}px`;
      card.parentNode?.insertBefore(placeholder, card);

      products.classList.add('is-product-overlaying');
      card.classList.add('is-overlay-active');
      card.style.setProperty('--q-single-card-overlay-top', `${targetTop}px`);
      card.style.setProperty('--q-single-card-overlay-left', `${targetLeft}px`);
      card.style.setProperty('--q-single-card-overlay-width', `${targetWidth}px`);
      card.style.transition = 'none';
      card.style.transformOrigin = 'center center';
      card.style.transform = `translate3d(${startCenterX - targetCenterX}px, ${startCenterY - targetCenterY}px, 0) scale(${scaleX}, ${scaleY})`;
      void card.offsetWidth;

      return true;
    };

    const updateExpandedProductSpace = (card) => {
      if (!products || !card?.classList.contains('is-expanded')) return;

      const info = card.querySelector('.q-single-product__info');
      if (!info || info.hidden) return;

      const productsRect = products.getBoundingClientRect();
      const infoRect = info.getBoundingClientRect();
      const productsPaddingBottom = Number.parseFloat(window.getComputedStyle(products).paddingBottom) || 0;
      const productsContentBottom = productsRect.bottom - productsPaddingBottom;
      const neededSpace = Math.max(0, Math.ceil(infoRect.bottom - productsContentBottom + 24));

      products.style.setProperty('--q-single-product-expanded-space', `${neededSpace}px`);
    };

    const scheduleExpandedProductSpace = (card) => {
      if (!card) return;
      const info = card.querySelector('.q-single-product__info');
      const duration = Math.max(300, settings.productCardMotionDuration || 700);

      window.requestAnimationFrame(() => {
        updateProductsEndSpace();
        updateExpandedProductSpace(card);
        window.requestAnimationFrame(() => {
          updateProductsEndSpace();
          updateExpandedProductSpace(card);
        });
      });

      [240, 620, duration, duration + 120].forEach((delay) => {
        window.setTimeout(() => {
          updateProductsEndSpace();
          updateExpandedProductSpace(card);
        }, delay);
      });

      info?.addEventListener('transitionend', (event) => {
        if (event.propertyName === 'transform') {
          updateProductsEndSpace();
          updateExpandedProductSpace(card);
        }
      }, { once: true });
    };

    const finishProductClose = (card, index) => {
      clearProductCloseTimer(index);
      card?.classList.remove('is-closing', 'is-expanded', 'is-info-expanded', 'is-info-closing');
      clearProductOverlay(card);
      setProductCardDetailsHidden(card, true);
      if (activeProductIndex === null && !products?.querySelector('.q-single-product.is-expanded')) {
        panel?.classList.remove('is-product-expanded');
        clearExpandedProductSpace();
        clearProductsEndSpace();
      }
      schedulePendingProductRender();
    };

    const collapseProductCard = (card, index, immediate = false) => {
      if (!card) return;

      clearProductCloseTimer(index);
      if (document.activeElement instanceof HTMLElement && card.contains(document.activeElement)) {
        document.activeElement.blur();
      }
      card.querySelector('[data-single-product-open]')?.setAttribute('aria-expanded', 'false');

      if (immediate || prefersReducedMotion()) {
        finishProductClose(card, index);
        return;
      }

      if (card.classList.contains('is-overlay-active')) {
        const placeholder = getProductPlaceholder(card);
        const infoCloseDuration = Math.min(520, Math.max(260, Math.round(settings.productCardMotionDuration * 0.34)));

        card.classList.remove('is-info-expanded');
        card.classList.add('is-info-closing');

        const startCardClose = () => {
          const cardRect = card.getBoundingClientRect();
          const placeholderRect = placeholder?.getBoundingClientRect();

          card.classList.add('is-closing');

          if (placeholderRect?.width && cardRect.width) {
            const cardCenterX = cardRect.left + cardRect.width / 2;
            const cardCenterY = cardRect.top + cardRect.height / 2;
            const placeholderCenterX = placeholderRect.left + placeholderRect.width / 2;
            const placeholderCenterY = placeholderRect.top + placeholderRect.height / 2;
            const closeTransform = `translate3d(${placeholderCenterX - cardCenterX}px, ${placeholderCenterY - cardCenterY}px, 0) scale(${placeholderRect.width / cardRect.width}, ${placeholderRect.height / cardRect.height})`;
            card.style.setProperty('--q-single-card-overlay-close-transform', closeTransform);
            card.style.removeProperty('transition');
            card.style.transform = 'none';
            void card.offsetWidth;
            card.style.removeProperty('transition');
            card.style.transform = closeTransform;
          }
        };

        const phaseTimer = window.setTimeout(startCardClose, infoCloseDuration);
        const finishTimer = window.setTimeout(() => finishProductClose(card, index), infoCloseDuration + settings.productCardMotionDuration);
        productCloseTimers.set(index, [phaseTimer, finishTimer]);
        return;
      }

      card.classList.remove('is-expanded');
      card.classList.add('is-closing');
      const timer = window.setTimeout(() => finishProductClose(card, index), settings.productCardMotionDuration);
      productCloseTimers.set(index, timer);
    };

    const openProductCard = (card, index) => {
      if (!card) {
        activeProductIndex = null;
        return;
      }

      clearProductCloseTimer(index);
      card.classList.remove('is-closing', 'is-info-closing', 'is-info-expanded');
      setProductCardDetailsHidden(card, false);
      card.querySelector('[data-single-product-open]')?.setAttribute('aria-expanded', 'true');
      panel?.classList.add('is-product-expanded');
      const hasOverlay = prepareProductOverlay(card);
      if (!hasOverlay) {
        setProductExpandedOffset(card);
      }

      window.requestAnimationFrame(() => {
        card.classList.add('is-expanded');
        if (hasOverlay) {
          card.classList.add('is-info-expanded');
          scheduleExpandedProductSpace(card);
          window.requestAnimationFrame(() => {
            card.style.removeProperty('transition');
            card.style.transform = 'none';
          });
        } else {
          scheduleExpandedProductSpace(card);
        }
      });
    };

    const closeExpandedProduct = (immediate = false) => {
      if (activeProductIndex === null) return;

      const index = activeProductIndex;
      const card = getProductCardByIndex(index);
      activeProductIndex = null;
      collapseProductCard(card, index, immediate);
    };

    const playProductSwitch = (direction = 'next') => {
      if (!products || prefersReducedMotion()) return;

      window.clearTimeout(productSwitchTimer);
      products.classList.remove('is-switching-next', 'is-switching-prev');
      void products.offsetWidth;
      products.classList.add(direction === 'prev' ? 'is-switching-prev' : 'is-switching-next');
      productSwitchTimer = window.setTimeout(() => {
        products.classList.remove('is-switching-next', 'is-switching-prev');
      }, settings.productCardMotionDuration);
    };

    const expandProductAt = (index, options = {}) => {
      const nextIndex = Number(index);
      if (Number.isNaN(nextIndex)) return;

      const previousIndex = activeProductIndex;
      if (previousIndex !== null) {
        collapseProductCard(getProductCardByIndex(previousIndex), previousIndex);
        playProductSwitch(options.direction || (nextIndex > previousIndex ? 'next' : 'prev'));
      }

      activeProductIndex = nextIndex;
      openProductCard(getProductCardByIndex(nextIndex), nextIndex);
    };

    const renderSingleProductCard = (product, index, productCount) => {
        const sortData = getProductSortData(product);
        const image = getProductCardImage(product);
        const variant = sortData.variant;
        const soldOut = isProductSoldOut(product);
        const price = getProductDisplayPrice(product, sortData);
        const url = `${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(product.handle)}`;
        const parsed = sortData.parsed;
        const title = getProductCardValue(product, settings.productCardTitleSource, parsed, variant) || product.title || '';
        const fields = settings.productCardFields
          .map((field) => ({
            label: field.label,
            value: getProductCardValue(product, field.source, parsed, variant),
          }))
          .filter((field) => field.label && field.value);
        const previousIndex = index <= 0 ? productCount - 1 : index - 1;
        const nextIndex = index >= productCount - 1 ? 0 : index + 1;
        const hasNav = productCount > 1;

        return `
          <article class="q-single-product${soldOut ? ' q-single-product--sold-out' : ''}" data-single-product-card data-catalog-grid-product-id="${escapeHtml(product.id)}" data-product-index="${index}" data-product-sold-out="${soldOut}">
            <button class="q-single-product__preview" type="button" data-single-product-open aria-expanded="false" aria-label="${escapeHtml(product.title || '')}">
              <span class="q-single-product__media">
              ${image.src ? `<img src="${escapeHtml(image.src)}" srcset="${escapeHtml(image.srcset)}" sizes="(min-width: 750px) 220px, 320px" alt="${escapeHtml(product.title || '')}" loading="lazy">` : ''}
              ${soldOut ? '<span class="q-single-product__sold-out-ribbon" aria-hidden="true">Ausverkauft</span>' : ''}
              </span>
            </button>
            <button class="q-single-product__close" type="button" data-single-product-close aria-label="${escapeHtml(settings.productCardCloseLabel)}" hidden>
              <span aria-hidden="true">X</span>
            </button>
            <div class="q-single-product__info" hidden>
              ${hasNav ? `
                <button class="q-single-product__nav q-single-product__nav--prev" type="button" data-single-product-nav="${previousIndex}" aria-label="${escapeHtml(settings.productCardPrevLabel)}">
                  <span aria-hidden="true">&#8249;</span>
                </button>
                <button class="q-single-product__nav q-single-product__nav--next" type="button" data-single-product-nav="${nextIndex}" aria-label="${escapeHtml(settings.productCardNextLabel)}">
                  <span aria-hidden="true">&#8250;</span>
                </button>
              ` : ''}
              <div class="q-single-product__details">
                <h3 class="q-single-product__title">${escapeHtml(title)}</h3>
                ${fields.length ? `
                  <div class="q-single-product__meta">
                    ${fields.map((field) => `
                      <span class="q-single-product__meta-chip">
                        <span>${escapeHtml(field.label)}</span>
                        <strong>${escapeHtml(field.value)}</strong>
                      </span>
                    `).join('')}
                  </div>
                ` : ''}
                ${soldOut ? '<p class="q-single-product__price q-single-product__price--sold-out">Ausverkauft</p>' : (price ? `<p class="q-single-product__price">${escapeHtml(price)}</p>` : '')}
                <div class="q-single-product__actions">
                  <a class="q-single-product__article q-btn q-btn--glass q-effect--shine" href="${escapeHtml(url)}" aria-label="${escapeHtml(settings.productCardArticleLabel)}" title="${escapeHtml(settings.productCardArticleLabel)}">
                    ${singleCardActionIcons.article}
                    <span class="visually-hidden">${escapeHtml(settings.productCardArticleLabel)}</span>
                  </a>
                  ${soldOut ? `
                    <button class="q-single-product__buy q-btn q-btn--glass is-sold-out" type="button" disabled aria-label="Ausverkauft" title="Ausverkauft">
                      <span class="visually-hidden">Ausverkauft</span>
                    </button>
                  ` : (!soldOut && variant?.id ? `
                    <form class="q-single-product__buy-form" action="${window.Shopify?.routes?.root || '/'}cart/add" method="post" data-product-form>
                      <input type="hidden" name="id" value="${escapeHtml(variant.id)}">
                      <input type="hidden" name="quantity" value="1">
                      <button class="q-single-product__buy q-btn q-btn--rose q-effect--pulse" type="submit" name="add" aria-label="${escapeHtml(settings.productCardBuyLabel)}" title="${escapeHtml(settings.productCardBuyLabel)}" data-add-to-cart-text="${escapeHtml(settings.productCardBuyLabel)}">
                        ${singleCardActionIcons.cart}
                        <span class="visually-hidden">${escapeHtml(settings.productCardBuyLabel)}</span>
                      </button>
                    </form>
                  ` : `
                    <a class="q-single-product__buy q-btn q-btn--rose q-effect--pulse" href="${escapeHtml(url)}" aria-label="${escapeHtml(settings.productCardBuyLabel)}" title="${escapeHtml(settings.productCardBuyLabel)}">
                      ${singleCardActionIcons.cart}
                      <span class="visually-hidden">${escapeHtml(settings.productCardBuyLabel)}</span>
                    </a>
                  `)}
                </div>
              </div>
            </div>
        </article>
        `;
    };

    const renderProducts = () => {
      if (!products) return;

      clearPendingProductRender();
      resetLoadMoreFooter();
      productCloseTimers.forEach(clearTimerEntry);
      productCloseTimers.clear();
      activeProductIndex = null;
      panel?.classList.remove('is-product-expanded');
      clearExpandedProductSpace();
      clearProductsEndSpace();
      clearProductReserveHeight();
      products.classList.remove('is-switching-next', 'is-switching-prev', 'is-product-overlaying');

      const filteredProducts = getFilteredSortedProducts();
      if (visibleProductCount > filteredProducts.length && (!productDataHasMore || !productDataSourceReliable)) {
        visibleProductCount = Math.max(1, Math.min(settings.productPageSize, filteredProducts.length));
      }
      const visibleProducts = filteredProducts.slice(0, visibleProductCount);
      const canLoadMoreProducts = filteredProducts.length > visibleProducts.length || (productDataHasMore && productDataSourceReliable);

      if (!visibleProducts.length) {
        const message = productDataIsLoadingMore ? labels.loading : (allProducts.length ? labels.emptyFilter : labels.empty);
        products.innerHTML = `<div class="q-single-products__empty">${escapeHtml(message)}</div>`;
        setBadge(formatCount(0, labels.cardSingular, labels.cardPlural));
        renderLoadMoreControl(0, hasActiveProductRefinement() ? 0 : getActiveTotalCount(), canLoadMoreProducts, hasActiveProductRefinement());
        clearProductsEndSpace();
        clearProductReserveHeight();
        return;
      }

      const refined = hasActiveProductRefinement();
      const displayTotal = refined ? filteredProducts.length : getActiveTotalCount();
      const visibleDisplayCount = Math.min(visibleProducts.length, displayTotal);

      setBadge(formatVisibleProductProgress(visibleDisplayCount, displayTotal, refined));
      renderLoadMoreControl(visibleDisplayCount, displayTotal, canLoadMoreProducts, refined);
      products.innerHTML = visibleProducts.map((product, index) => renderSingleProductCard(product, index, visibleProducts.length)).join('');

      window.requestAnimationFrame(() => {
        updateProductsEndSpace();
        updateProductsReserveHeight();
      });
    };

    const scheduleCardTypeProductRefresh = (delay = 0) => {
      window.clearTimeout(cardTypeRenderTimer);
      const scheduledHandle = activeHandle;
      const refreshProducts = () => {
        cardTypeRenderTimer = null;
        if (!scheduledHandle || activeHandle !== scheduledHandle) return;

        closeExpandedProduct(true);
        visibleProductCount = settings.productPageSize;
        renderProducts();
        scheduleProductSearchDataLoad();
      };

      if (delay > 0) {
        cardTypeRenderTimer = window.setTimeout(refreshProducts, delay);
        return;
      }

      refreshProducts();
    };

    const getProductIdentity = (product = {}) => String(product.id || product.handle || product.url || '');

    const mergeUniqueProducts = (existing = [], incoming = []) => {
      const seen = new Set(existing.map(getProductIdentity).filter(Boolean));
      const uniqueIncoming = incoming.filter((product) => {
        const identity = getProductIdentity(product);
        if (!identity || seen.has(identity)) return false;
        seen.add(identity);
        return true;
      });
      return existing.concat(uniqueIncoming);
    };

    const fetchCollectionProductsBatch = async (handle, options = {}) => {
      await catalogRestore;
      return (await catalogSource.page(handle, Math.max(1, Number(options.page || 1)))).products;
    };

    const fetchCollectionProducts = async (handle) => fetchCollectionProductsBatch(handle, { page: 1 });

    const fetchCollectionProductsWindow = async (handle, { page, limit, knownTotal = 0, signal = null }) => {
      signal?.throwIfAborted();
      const remainingPages = knownTotal > 0 ? Math.ceil(knownTotal / limit) - page + 1 : 1;
      const pageCount = Math.max(1, Math.min(3, remainingPages));
      const pageNumbers = Array.from({ length: pageCount }, (_, index) => page + index);
      // Drain the window before retrying; a short page makes later results irrelevant.
      const pageResults = await Promise.allSettled(pageNumbers.map((nextPage) => (
        fetchCollectionProductsBatch(handle, { page: nextPage, limit, signal })
      )));
      signal?.throwIfAborted();

      const products = [];
      let pagesLoaded = 0;
      let reachedEnd = false;
      for (const result of pageResults) {
        if (result.status === 'rejected') throw result.reason;
        const nextProducts = result.value;
        products.push(...nextProducts);
        pagesLoaded += 1;
        if (nextProducts.length < limit) {
          reachedEnd = true;
          break;
        }
      }
      return { products, pagesLoaded, reachedEnd };
    };

    const getLiveSearchProducts = async () => {
      const requestToken = activeCollectionRequestToken;
      const productsList = await catalog.ensure();
      if (!singleCardsDisposed && activeHandle === root.dataset.baseHandle && requestToken === activeCollectionRequestToken) {
        const grew = productsList.length > allProducts.length;
        allProducts = productsList;
        activeKnownComplete = true;
        activeTotalCount = productsList.length;
        productDataHasMore = false;
        productDataPage = catalog.getPages().length;
        if (grew) requestProductRenderAfterHydration();
      }
      return productsList;
    };

    const loadCollection = async (handle, label, button = null) => {
      if (!handle || singleCardsDisposed) return;

      if (activeHandle === handle && button) {
        return;
      }

      clearPendingProductRender();

      buttons.forEach((setButton) => {
        setButton.classList.remove('is-active');
        setButton.setAttribute('aria-pressed', 'false');
      });
      if (button) {
        button.classList.add('is-active');
        button.setAttribute('aria-pressed', 'true');
      }
      currentSort = normalizeProductSort(button ? settings.productDefaultSort : settings.baseProductDefaultSort);
      activeHandle = handle;
      activeButton = button;
      activeTotalCount = Number(button?.dataset.totalCount || 0) || (button ? 0 : Number(root.dataset.baseTotalCount || 0) || 0);
      activeKnownComplete = false;
      activeCollectionRequestToken += 1;
      const requestToken = activeCollectionRequestToken;
      activeProductIndex = null;
      allProducts = [];
      visibleProductCount = settings.productPageSize;
      productDataPage = 1;
      productDataHasMore = false;
      productDataIsLoadingMore = false;
      productDataSourceReliable = true;
      window.clearTimeout(searchDataLoadTimer);
      searchDataLoadRequestId += 1;
      sortCache = new WeakMap();
      ensureSortState();
      resetLoadMoreFooter();
      if (panelName) panelName.textContent = label || handle;
      setBadge(labels.loading);
      renderSkeletons(8);
      panel?.classList.add('is-open');
      panel?.classList.add('is-loading');
      panel?.classList.remove('is-loaded');
      if (button && settings.scroll && panel) {
        window.setTimeout(() => {
          panel.scrollIntoView({
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            block: 'start',
          });
        }, 120);
      }

      try {
        const nextProducts = await fetchCollectionProducts(handle);
        if (singleCardsDisposed || activeCollectionRequestToken !== requestToken || activeHandle !== handle) return;

        allProducts = nextProducts;
        productDataHasMore = allProducts.length >= Number(root.dataset.productLimit || 248);
        activeKnownComplete = !productDataHasMore;
        if (!allProducts.length) visibleProductCount = 0;
        renderProducts();
        scheduleProductSearchDataLoad();
        panel?.classList.remove('is-loading');
        panel?.classList.add('is-loaded');
      } catch {
        if (singleCardsDisposed || activeCollectionRequestToken !== requestToken || activeHandle !== handle) return;
        if (products) products.innerHTML = `<div class="q-single-products__empty">${escapeHtml(labels.error)}</div>`;
        setBadge('');
        panel?.classList.remove('is-loading');
      }
    };

    function closePanel() {
      closeExpandedProduct(true);
      clearPendingProductRender();
      resetLoadMoreFooter();
      activeCollectionRequestToken += 1;
      panel?.classList.remove('is-open');
      clearProductReserveHeight();
      products?.classList.remove('is-product-overlaying');
      buttons.forEach((button) => {
        button.classList.remove('is-active');
        button.setAttribute('aria-pressed', 'false');
      });
      activeHandle = null;
      activeButton = null;
      activeProductIndex = null;
      allProducts = [];
      visibleProductCount = settings.productPageSize;
      productDataPage = 1;
      productDataHasMore = false;
      productDataIsLoadingMore = false;
      productDataSourceReliable = true;
      window.clearTimeout(searchDataLoadTimer);
      searchDataLoadRequestId += 1;
      sortCache = new WeakMap();
      panel?.classList.remove('is-loading');
      panel?.classList.remove('is-loaded');
    }

    const loadSet = (button) => {
      const handle = button?.dataset.handle;
      if (!handle) return;

      const wasActive = button.classList.contains('is-active') || activeHandle === handle;
      if (wasActive) {
        const baseHandle = root.dataset.baseHandle;
        if (baseHandle) {
          loadCollection(baseHandle, root.dataset.baseLabel || 'Alle Einzelkarten', null);
        } else {
          closePanel();
        }
        return;
      }

      loadCollection(button.dataset.handle, button.dataset.label, button);
      closeSetsDisclosure({ spring: true });
      button.classList.remove('is-activating');
      void button.offsetWidth;
      button.classList.add('is-activating');
      window.setTimeout(() => button.classList.remove('is-activating'), 480);
    };

    const filterSets = (query) => {
      const term = query.toLowerCase().trim();
      let visible = 0;

      buttons.forEach((button) => {
        const matches = !term || (button.dataset.title || '').includes(term);
        button.classList.toggle('is-hidden', !matches);
        if (matches) visible += 1;
      });

      categoryGroups.forEach((group) => {
        const groupHasVisibleSet = Array.from(group.querySelectorAll('[data-single-set]'))
          .some((button) => !button.classList.contains('is-hidden'));
        group.classList.toggle('is-hidden', term.length > 0 && !groupHasVisibleSet);
      });

      gridEmpty?.classList.toggle('is-visible', visible === 0 && (term.length > 0 || totalSets === 0));
      if (countTarget) {
        countTarget.textContent = term
          ? labels.visibleSets
            .replace('[visible]', visible)
            .replace('[total]', totalSets)
          : formatCount(totalSets, labels.setSingular, labels.setPlural);
      }
      clearButton?.classList.toggle('is-visible', term.length > 0);
    };

    const spawnRipple = (button, clientX, clientY) => {
      const rect = button.getBoundingClientRect();
      const ripple = document.createElement('span');
      ripple.className = 'q-single-ripple';
      ripple.style.left = `${clientX - rect.left}px`;
      ripple.style.top = `${clientY - rect.top}px`;
      button.appendChild(ripple);
      window.setTimeout(() => ripple.remove(), 800);
    };

    const spawnParticles = (button) => {
      if (!settings.particles) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      const rect = button.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const palette = ['#a78bfa', '#fbbf24', '#f472b6', '#34d399', '#60a5fa', '#ffffff', '#fb923c'];

      const particleCount = particleCountByIntensity[settings.singleEffectIntensity] || particleCountByIntensity.balanced;

      for (let index = 0; index < particleCount; index += 1) {
        const particle = document.createElement('span');
        const size = 3 + Math.random() * 4;
        const color = palette[Math.floor(Math.random() * palette.length)];
        particle.style.cssText = `position:fixed;border-radius:50%;pointer-events:none;z-index:99999;width:${size}px;height:${size}px;background:${color};box-shadow:0 0 7px ${color};left:${centerX}px;top:${centerY}px;transform:translate(-50%,-50%)`;
        document.body.appendChild(particle);
        const angle = ((Math.PI * 2) / particleCount) * index + (Math.random() - 0.5) * 0.7;
        const distance = 30 + Math.random() * (settings.singleEffectIntensity === 'strong' ? 86 : 58);
        const x = Math.cos(angle) * distance;
        const y = Math.sin(angle) * distance;
        window.requestAnimationFrame(() => {
          particle.style.transition = 'transform .72s cubic-bezier(0,.9,.57,1), opacity .72s ease';
          particle.style.transform = `translate(calc(-50% + ${x}px), calc(-50% + ${y}px)) scale(0)`;
          particle.style.opacity = '0';
        });
        window.setTimeout(() => particle.remove(), 820);
      }
    };

    closeButton?.addEventListener('click', closePanel);
    loadMoreButton?.addEventListener('click', () => {
      requestNextProductBatch();
    });

    const toggleProductCard = (index) => {
      const nextIndex = Number(index);
      if (Number.isNaN(nextIndex)) return;

      if (activeProductIndex === nextIndex) {
        closeExpandedProduct();
        return;
      }

      expandProductAt(nextIndex);
    };

    products?.addEventListener('click', (event) => {
      const closeTrigger = event.target.closest('[data-single-product-close]');
      if (closeTrigger) {
        event.preventDefault();
        closeExpandedProduct();
        return;
      }

      const navTrigger = event.target.closest('[data-single-product-nav]');
      if (navTrigger) {
        event.preventDefault();
        expandProductAt(Number(navTrigger.dataset.singleProductNav), {
          direction: navTrigger.classList.contains('q-single-product__nav--prev') ? 'prev' : 'next',
        });
        return;
      }

      const openTrigger = event.target.closest('[data-single-product-open]');
      if (openTrigger) {
        event.preventDefault();
        const card = openTrigger.closest('[data-single-product-card]');
        toggleProductCard(Number(card?.dataset.productIndex || 0));
      }
    });

    panel?.addEventListener('click', (event) => {
      if (activeProductIndex === null) return;
      if (event.target.closest('.q-single-product')) return;
      if (event.target.closest('.q-single-panel__header')) return;
      closeExpandedProduct();
    });

    root.addEventListener('click', (event) => {
      if (activeProductIndex === null) return;
      if (event.target.closest('.q-single-product')) return;
      if (event.target.closest('[data-single-panel]')) return;
      closeExpandedProduct();
    });

    const getDisclosureTargetHeight = (disclosurePanel) => {
      const computedStyle = window.getComputedStyle(disclosurePanel);
      const maxHeight = Number.parseFloat(computedStyle.maxHeight);
      return Number.isFinite(maxHeight)
        ? Math.min(disclosurePanel.scrollHeight, maxHeight)
        : disclosurePanel.scrollHeight;
    };

    const cleanupDisclosureSpring = (disclosurePanel) => {
      disclosurePanel.classList.remove('is-springing-open', 'is-springing-closed');
      disclosurePanel.style.removeProperty('height');
      disclosurePanel.style.removeProperty('--q-single-disclosure-height');
      delete disclosurePanel.dataset.disclosureAnimation;
    };

    const playDisclosureOpenSpring = (disclosurePanel) => {
      if (!disclosurePanel || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

      const targetHeight = getDisclosureTargetHeight(disclosurePanel);

      cleanupDisclosureSpring(disclosurePanel);
      disclosurePanel.dataset.disclosureAnimation = 'open';
      disclosurePanel.style.removeProperty('height');
      disclosurePanel.style.setProperty('--q-single-disclosure-height', `${Math.ceil(targetHeight)}px`);
      void disclosurePanel.offsetHeight;
      disclosurePanel.classList.add('is-springing-open');
      disclosurePanel.addEventListener('animationend', () => {
        if (disclosurePanel.dataset.disclosureAnimation === 'open') {
          cleanupDisclosureSpring(disclosurePanel);
        }
      }, { once: true });
    };

    const playDisclosureCloseSpring = (disclosurePanel, onComplete) => {
      if (!disclosurePanel) return;

      const finishCloseWithoutMotion = () => {
        cleanupDisclosureSpring(disclosurePanel);
        onComplete?.();
      };

      if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
        finishCloseWithoutMotion();
        return;
      }

      const targetHeight = getDisclosureTargetHeight(disclosurePanel);
      const animationToken = `close-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      let finished = false;
      let fallbackTimer;

      const finishClose = () => {
        if (finished || disclosurePanel.dataset.disclosureAnimation !== animationToken) return;
        finished = true;
        window.clearTimeout(fallbackTimer);
        cleanupDisclosureSpring(disclosurePanel);
        onComplete?.();
      };

      cleanupDisclosureSpring(disclosurePanel);
      disclosurePanel.dataset.disclosureAnimation = animationToken;
      disclosurePanel.style.setProperty('--q-single-disclosure-height', `${Math.ceil(targetHeight)}px`);
      disclosurePanel.style.height = `${Math.ceil(targetHeight)}px`;
      void disclosurePanel.offsetHeight;
      disclosurePanel.classList.add('is-springing-closed');
      disclosurePanel.addEventListener('animationend', finishClose, { once: true });
      fallbackTimer = window.setTimeout(finishClose, 760);
    };

    const setDisclosureState = (toggle, disclosurePanel, open, options = {}) => {
      if (!toggle || !disclosurePanel) return;

      toggle.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', String(open));

      if (!open) {
        const shouldSpringClose = Boolean(options.spring && !disclosurePanel.hidden);
        if (shouldSpringClose) {
          playDisclosureCloseSpring(disclosurePanel, () => {
            disclosurePanel.hidden = true;
            options.onCloseComplete?.();
          });
        } else {
          cleanupDisclosureSpring(disclosurePanel);
          disclosurePanel.hidden = true;
          options.onCloseComplete?.();
        }
        return;
      }

      const shouldSpringOpen = Boolean(options.spring && disclosurePanel.hidden);
      disclosurePanel.hidden = false;

      if (shouldSpringOpen) {
        playDisclosureOpenSpring(disclosurePanel);
      } else {
        cleanupDisclosureSpring(disclosurePanel);
      }
    };

    const getSetCategoryPanel = (categoryToggle) => {
      const panelId = categoryToggle?.getAttribute('aria-controls');
      return panelId ? document.getElementById(panelId) : null;
    };

    const closeSetCategoryPanels = (options = {}) => {
      categoryToggles.forEach((categoryToggle) => {
        if (options.except === categoryToggle) return;

        const categoryPanel = getSetCategoryPanel(categoryToggle);
        if (!categoryPanel) {
          categoryToggle.classList.remove('is-open');
          categoryToggle.setAttribute('aria-expanded', 'false');
          return;
        }

        setDisclosureState(categoryToggle, categoryPanel, false, {
          spring: options.spring !== false,
        });
      });
    };

    const closeSetsDisclosure = (options = {}) => {
      if (!setsToggle || !setsPanel || setsPanel.hidden) return;

      setDisclosureState(setsToggle, setsPanel, false, {
        spring: options.spring !== false,
        onCloseComplete: () => closeSetCategoryPanels({ spring: false }),
      });
    };

    const preloadSetPanelImages = () => {
      if (!setsPanel || setsPanel.dataset.imagesPreloading === 'true') return;

      setsPanel.dataset.imagesPreloading = 'true';
      Array.from(setsPanel.querySelectorAll('img')).slice(0, settings.setPreloadLimit).forEach((image) => {
        image.loading = 'eager';
        if ('fetchPriority' in image) {
          image.fetchPriority = 'low';
        }

        const source = image.currentSrc || image.getAttribute('src');
        if (!source) return;

        const preload = new Image();
        preload.decoding = 'async';
        preload.src = source;
        preload.decode?.().catch(() => {});
      });
    };

    if (setsPanel) {
      const scheduleSetPreload = window.requestIdleCallback?.bind(window) || ((callback) => window.setTimeout(callback, 180));
      scheduleSetPreload(preloadSetPanelImages, { timeout: 1200 });
      setsToggle?.addEventListener('pointerenter', preloadSetPanelImages);
      setsToggle?.addEventListener('focus', preloadSetPanelImages);
      setsToggle?.addEventListener('touchstart', preloadSetPanelImages, { passive: true });
    }

    setDisclosureState(setsToggle, setsPanel, setsPanel ? !setsPanel.hidden : false);
    categoryToggles.forEach((categoryToggle) => {
      const categoryPanel = getSetCategoryPanel(categoryToggle);
      setDisclosureState(categoryToggle, categoryPanel, categoryPanel ? !categoryPanel.hidden : false);
    });

    setsToggle?.addEventListener('click', () => {
      if (!setsPanel) return;
      preloadSetPanelImages();
      const openSetsPanel = setsPanel.hidden;
      setDisclosureState(setsToggle, setsPanel, openSetsPanel, {
        spring: true,
        onCloseComplete: () => closeSetCategoryPanels({ spring: false }),
      });
    });

    const bindSingleCardInteraction = (button, options = {}) => {
      const shine = button.querySelector('.q-single-set-card__shine');
      let leaveTimer;
      let activationTimer;
      let hoverReleaseTimer;
      const interactionScale = options.scale || 'set';
      const allowTransform = options.enableTransform !== false;
      const allowActivation = options.enableActivation !== false;
      const suppressHoverAfterDeactivation = options.suppressHoverAfterDeactivation === true;

      const clearHoverSuppression = () => {
        window.clearTimeout(hoverReleaseTimer);
        button.classList.remove('is-hover-suppressed');
      };

      button.addEventListener('click', (event) => {
        if (suppressHoverAfterDeactivation) clearHoverSuppression();
        const rect = button.getBoundingClientRect();
        spawnRipple(button, event.clientX || rect.left + rect.width / 2, event.clientY || rect.top + rect.height / 2);
        if (allowActivation) {
          window.clearTimeout(activationTimer);
          button.classList.remove('is-activating');
          void button.offsetWidth;
          button.classList.add('is-activating');
          activationTimer = window.setTimeout(() => button.classList.remove('is-activating'), 480);
        }
        options.onClick?.(event);
        if (suppressHoverAfterDeactivation && button.getAttribute('aria-pressed') === 'false') {
          hoverReleaseTimer = window.setTimeout(() => {
            if (button.getAttribute('aria-pressed') !== 'false') return;
            button.classList.remove('is-hovered');
            button.classList.add('is-hover-suppressed');
          }, 480);
        }
      });

      button.addEventListener('mouseenter', () => {
        if (suppressHoverAfterDeactivation) clearHoverSuppression();
        window.clearTimeout(leaveTimer);
        button.classList.remove('is-leaving');
        button.classList.remove('is-hovered');
        void button.offsetWidth;
        button.classList.add('is-hovered');
        spawnParticles(button);
      });

      button.addEventListener('mousemove', (event) => {
        const rect = button.getBoundingClientRect();
        const deltaX = (event.clientX - rect.left - rect.width / 2) / (rect.width / 2);
        const deltaY = (event.clientY - rect.top - rect.height / 2) / (rect.height / 2);
        if (allowTransform && settings.tilt) {
          const baseTilt = tiltByIntensity[settings.singleEffectIntensity] || tiltByIntensity.balanced;
          const tiltAmount = interactionScale === 'filter' ? baseTilt * 0.62 : baseTilt;
          const scale = interactionScale === 'filter'
            ? (settings.singleEffectIntensity === 'strong' ? 1.04 : settings.singleEffectIntensity === 'subtle' ? 1.018 : 1.032)
            : (settings.singleEffectIntensity === 'strong' ? 1.058 : settings.singleEffectIntensity === 'subtle' ? 1.025 : 1.045);
          button.style.transform = `perspective(600px) rotateX(${-deltaY * tiltAmount}deg) rotateY(${deltaX * tiltAmount}deg) scale(${scale})`;
        }
        shine?.style.setProperty('--sx', `${(((event.clientX - rect.left) / rect.width) * 100).toFixed(1)}%`);
        shine?.style.setProperty('--sy', `${(((event.clientY - rect.top) / rect.height) * 100).toFixed(1)}%`);
        button.style.setProperty('--sx', `${(((event.clientX - rect.left) / rect.width) * 100).toFixed(1)}%`);
        button.style.setProperty('--sy', `${(((event.clientY - rect.top) / rect.height) * 100).toFixed(1)}%`);
        button.style.setProperty('--holo-angle', `${Math.atan2(deltaY, deltaX) * (180 / Math.PI) + 115}deg`);
      });

      button.addEventListener('mouseleave', () => {
        if (suppressHoverAfterDeactivation) clearHoverSuppression();
        button.classList.remove('is-hovered');
        button.classList.add('is-leaving');
        if (allowTransform) {
          button.style.transform = '';
        }
        leaveTimer = window.setTimeout(() => button.classList.remove('is-leaving'), 600);
      });

      button.addEventListener('touchstart', (event) => {
        const touch = event.touches[0];
        spawnRipple(button, touch.clientX, touch.clientY);
        if (allowTransform) {
          button.style.transition = 'transform .18s ease';
          button.style.transform = 'scale(0.93)';
          window.setTimeout(() => {
            button.style.transition = 'transform .38s cubic-bezier(0.34,1.56,0.64,1)';
            button.style.transform = '';
          }, 180);
        }
      }, { passive: true });
    };

    categoryToggles.forEach((categoryToggle) => {
      bindSingleCardInteraction(categoryToggle, {
        scale: 'filter',
        enableTransform: false,
        onClick: () => {
          const categoryPanel = getSetCategoryPanel(categoryToggle);
          if (!categoryPanel) return;

          const openCategoryPanel = categoryPanel.hidden;
          if (openCategoryPanel) {
            closeSetCategoryPanels({ except: categoryToggle, spring: true });
          }

          setDisclosureState(categoryToggle, categoryPanel, openCategoryPanel, { spring: true });
        },
      });
    });

    buttons.forEach((button) => {
      bindSingleCardInteraction(button, {
        onClick: () => loadSet(button),
      });
    });

    chips.forEach((chip) => {
      bindSingleCardInteraction(chip, {
        scale: 'filter',
        enableTransform: false,
        enableActivation: true,
        suppressHoverAfterDeactivation: true,
        onClick: () => {
          const filter = chip.dataset.singleFilter;
          if (!filter) return;
          setActiveCardType(getNextCardType(filter));
          scheduleCardTypeProductRefresh(cardTypeFilterFeedbackDuration);
        },
      });
    });

    soldOutToggle?.addEventListener('change', () => {
      showSoldOutProducts = soldOutToggle.checked;
      if (!activeHandle) return;

      closeExpandedProduct(true);
      visibleProductCount = settings.productPageSize;
      renderProducts();
      scheduleProductSearchDataLoad();
    });

    if (sortSelect) {
      sortSelect.addEventListener('change', () => {
        currentSort = normalizeProductSort(sortSelect.value);
        visibleProductCount = settings.productPageSize;
        sortCache = new WeakMap();
        renderProducts();
      });
    }

    searchInput?.addEventListener('input', () => {
      currentProductSearchTerm = searchInput.value;
      preparedProductQuery = catalog.prepareQuery(currentProductSearchTerm);
      filterSets(searchInput.value);
      scheduleSingleLiveSearch(searchInput.value);
      if (activeHandle) {
        visibleProductCount = settings.productPageSize;
        renderProducts();
        scheduleProductSearchDataLoad();
      }
    });

    clearButton?.addEventListener('click', () => {
      if (!searchInput) return;
      searchInput.value = '';
      currentProductSearchTerm = '';
      preparedProductQuery = catalog.prepareQuery('');
      window.clearTimeout(searchDataLoadTimer);
      searchDataLoadRequestId += 1;
      filterSets('');
      hideSingleLiveSearch();
      if (activeHandle) {
        visibleProductCount = settings.productPageSize;
        renderProducts();
        scheduleProductSearchDataLoad();
      }
      searchInput.focus();
    });

    const refreshedHandles = new Set();
    const refreshingProducts = new Map();
    const refreshQueue = [];
    let activeRefreshes = 0;
    const refreshCatalogProduct = (handle, variantId = '', force = false) => {
      if (!handle || singleCardsDisposed) return;
      if (refreshingProducts.has(handle)) {
        const pending = refreshingProducts.get(handle);
        if (force) { pending.rerun = true; pending.variantId = variantId; }
        return pending.promise;
      }
      if (!force && refreshedHandles.has(handle)) return;
      const product = catalog.getProducts().find((item) => item.handle === handle) || allProducts.find((item) => item.handle === handle);
      if (!product) return;
      refreshedHandles.add(handle);
      const job = { product, variantId, requestedAt: Date.now(), rerun: false };
      const promise = new Promise((resolve) => { job.resolve = resolve; });
      job.promise = promise;
      refreshQueue.push(job);
      refreshingProducts.set(handle, job);
      pumpCatalogRefreshes();
      return promise;
    };
    const pumpCatalogRefreshes = () => {
      while (!singleCardsDisposed && activeRefreshes < 2 && refreshQueue.length) {
        const job = refreshQueue.shift();
        const handle = job.product.handle;
        activeRefreshes++;
        fetch((window.Shopify?.routes?.root || '/') + 'products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } }).then(async (response) => {
          if (!response.ok) return;
          const fresh = normalizeFreshCatalogProduct(job.product, await response.json(), job.variantId);
          if (!fresh || singleCardsDisposed || job.rerun) return;
          catalog.replaceProduct(fresh);
          allProducts = allProducts.map((product) => String(product.id) === String(fresh.id) ? fresh : product);
          sortCache.delete(job.product);
          const row = liveSearchResults?.querySelector('[data-catalog-product-id="' + fresh.id + '"]');
          if (row) {
            const focused = row.contains(document.activeElement) ? document.activeElement?.tagName : null;
            const template = document.createElement('template');
            template.innerHTML = renderSingleLiveSearchRow(fresh).trim();
            const replacement = template.content.firstElementChild;
            row.replaceWith(replacement);
            if (focused) replacement.querySelector(focused.toLowerCase())?.focus({ preventScroll: true });
          }
          const gridCard = products?.querySelector('[data-catalog-grid-product-id="' + fresh.id + '"]');
          if (gridCard) {
            const details = gridCard.querySelector('.q-single-product__details');
            const focused = details?.contains(document.activeElement) ? document.activeElement?.tagName : null;
            const template = document.createElement('template');
            template.innerHTML = renderSingleProductCard(fresh, Number(gridCard.dataset.productIndex), products.querySelectorAll('[data-single-product-card]').length).trim();
            const replacementDetails = template.content.querySelector('.q-single-product__details');
            if (details && replacementDetails) {
              details.replaceWith(replacementDetails);
              if (focused) (replacementDetails.querySelector(focused.toLowerCase() + ':not(:disabled)') || replacementDetails.querySelector('a'))?.focus({ preventScroll: true });
            }
            const soldOut = isProductSoldOut(fresh);
            gridCard.dataset.productSoldOut = String(soldOut);
            gridCard.classList.toggle('q-single-product--sold-out', soldOut);
            gridCard.querySelector('.q-single-product__sold-out-ribbon')?.remove();
            const ribbon = template.content.querySelector('.q-single-product__sold-out-ribbon');
            if (ribbon) gridCard.querySelector('.q-single-product__media')?.append(ribbon);
          }
          try {
            const key = catalogDirtyPrefix + catalogContext;
            const dirty = JSON.parse(sessionStorage.getItem(key) || '{}');
            if (dirty[handle] <= job.requestedAt) delete dirty[handle];
            if (Object.keys(dirty).length) sessionStorage.setItem(key, JSON.stringify(dirty));
            else sessionStorage.removeItem(key);
          } catch { /* RAM search still works. */ }
        }).catch(() => {}).finally(() => {
          refreshingProducts.delete(handle); activeRefreshes--; job.resolve();
          if (job.rerun) refreshCatalogProduct(handle, job.variantId, true);
          pumpCatalogRefreshes();
        });
      }
    };
    const refreshDirtyCatalogProducts = () => {
      try { Object.keys(JSON.parse(sessionStorage.getItem(catalogDirtyPrefix + catalogContext) || '{}')).forEach((handle) => refreshCatalogProduct(handle, '', true)); }
      catch { /* No session store: nothing to restore. */ }
    };
    const onCatalogCartConfirmed = (event) => refreshCatalogProduct(event.detail?.productHandle, event.detail?.variantId, true);
    const onCatalogCartError = (event) => {
      const variantId = event.detail?.variantId;
      const product = catalog.getProducts().find((item) => item.variants?.some((variant) => String(variant.id) === String(variantId)));
      if (product) refreshCatalogProduct(product.handle, variantId, true);
    };
    const onCatalogPageShow = (event) => { if (event.persisted) refreshDirtyCatalogProducts(); };

    let productResizeTimer;
    let productResizeWidth = window.innerWidth;
    const onSingleCardsResize = () => {
      window.clearTimeout(productResizeTimer);
      productResizeTimer = window.setTimeout(() => {
        const nextWidth = window.innerWidth;
        const widthChanged = Math.abs(nextWidth - productResizeWidth) > 8;
        productResizeWidth = nextWidth;

        if (widthChanged && activeProductIndex !== null) {
          if (products?.classList.contains('is-product-overlaying')) {
            closeExpandedProduct(true);
          } else {
            setProductExpandedOffset(getProductCardByIndex(activeProductIndex));
          }
        }
        updateProductsEndSpace();
        updateProductsReserveHeight();
      }, 120);
    };

    const sourceOptions = { fetch: window.fetch.bind(window), routeRoot: window.Shopify?.routes?.root || '/', productLimit: Number(root.dataset.productLimit || 248) };
    catalogContext = JSON.stringify([location.origin, sourceOptions.routeRoot, root.dataset.baseHandle, sourceOptions.productLimit, root.dataset.searchLanguage || document.documentElement.lang, root.dataset.searchCountry || '', root.dataset.searchCurrency || window.Shopify?.currency?.active, 'catalog-v3-condition-display-2026-10-26', window.Shopify?.theme?.id || '', getProductParseConfig(), settings.productCardFields, root.dataset.baseTotalCount || '', root.dataset.searchBoostEnabled === 'true' && !window.Shopify?.designMode]);
    let session = null;
    if (!window.Shopify?.designMode && root.dataset.searchBoostEnabled === 'true') {
      try { session = window.QCatalogSearchSession.create({ indexedDB: window.indexedDB, sessionStorage: window.sessionStorage, contextKey: catalogContext }); }
      catch { session = { load: async () => null, save: async () => ({ saved: false }) }; }
    }
    catalog = window.QCatalogSearchCore.create({ contextKey: catalogContext, collectionHandle: root.dataset.baseHandle, source: window.QCatalogSearchData.create(sourceOptions), session, totalHint: Number(root.dataset.baseTotalCount || 0), normalize: normalizeSingleCardSearchQuery, buildText: (product) => {
      const parsed = parseTcgCardTitle(product, getProductParseConfig());
      return getProductSearchText(product, parsed, getPrimaryVariant(product));
    } });
    catalogSource = catalog.getSource();
    preparedProductQuery = catalog.prepareQuery(currentProductSearchTerm);
    catalogRestore = catalog.restore().then((result) => {
      catalogWasRestored = result.restored;
      if (result.restored) refreshDirtyCatalogProducts();
      return result;
    });
    const catalogUI = window.QCatalogSearchUI.mount(root, catalog);
    const illustratorUI = window.QCatalogIllustratorUI?.mount(root, {
      catalog,
      onChange: (keys) => {
        activeIllustrators = new Set(keys);
        if (!activeHandle) loadCollection(root.dataset.baseHandle, root.dataset.baseLabel || 'Alle Einzelkarten', null);
        else scheduleCardTypeProductRefresh();
        scheduleSingleLiveSearch(searchInput?.value || '');
      },
    });
    document.addEventListener('quitchibo:cart-confirmed', onCatalogCartConfirmed);
    document.addEventListener('quitchibo:add-to-cart-error', onCatalogCartError);
    window.addEventListener('pageshow', onCatalogPageShow);
    window.addEventListener('resize', onSingleCardsResize);
    root._disposeCatalogSearch = () => {
      singleCardsDisposed = true;
      activeCollectionRequestToken++;
      hideSingleLiveSearch();
      window.clearTimeout(searchDataLoadTimer);
      window.clearTimeout(productResizeTimer);
      clearPendingProductRender();
      catalogUI.destroy();
      illustratorUI?.destroy();
      catalog.dispose();
      document.removeEventListener('quitchibo:cart-confirmed', onCatalogCartConfirmed);
      document.removeEventListener('quitchibo:add-to-cart-error', onCatalogCartError);
      window.removeEventListener('pageshow', onCatalogPageShow);
      window.removeEventListener('resize', onSingleCardsResize);
    };
    filterSets('');
    if (settings.autoload && root.dataset.baseHandle) {
      loadCollection(root.dataset.baseHandle, root.dataset.baseLabel || 'Alle Einzelkarten', null);
    }
  };

  const syncProductVariantToMedia = (gallery, mediaId) => {
    if (!gallery || !mediaId) return;

    const productLayout = gallery.closest('.q-product-layout');
    const variantSelect = productLayout?.querySelector('[data-product-variant-select]');
    if (!variantSelect) return;

    const mediaNode = Array.from(gallery.querySelectorAll('[data-product-media-panel], [data-product-media-thumb]'))
      .find((node) => node.dataset.productMediaPanel === String(mediaId) || node.dataset.productMediaThumb === String(mediaId));
    const variantIds = String(mediaNode?.dataset.productMediaVariantIds || '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);

    if (!variantIds.length || variantIds.includes(String(variantSelect.value))) return;

    const nextOption = Array.from(variantSelect.options)
      .find((option) => variantIds.includes(String(option.value)));
    if (!nextOption) return;

    variantSelect.value = nextOption.value;
    variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const setProductGalleryActiveMedia = (gallery, mediaId, options = {}) => {
    if (!gallery || !mediaId) return false;

    const panels = Array.from(gallery.querySelectorAll('[data-product-media-panel]'));
    const thumbs = Array.from(gallery.querySelectorAll('[data-product-media-thumb]'));
    const matchingPanel = panels.find((panel) => panel.dataset.productMediaPanel === String(mediaId));

    if (!matchingPanel) return false;

    const pausePanelMedia = (panel) => {
      panel.querySelectorAll('video').forEach((video) => video.pause());
    };

    panels.forEach((panel) => {
      const isActive = panel.dataset.productMediaPanel === String(mediaId);
      panel.hidden = !isActive;
      panel.classList.toggle('is-active', isActive);
      if (!isActive) pausePanelMedia(panel);
    });

    thumbs.forEach((thumb) => {
      const isActive = thumb.dataset.productMediaThumb === String(mediaId);
      thumb.classList.toggle('is-active', isActive);
      thumb.setAttribute('aria-pressed', String(isActive));
    });

    panels.forEach((panel) => {
      if (!panel.matches('[data-product-zoom-trigger]')) return;
      const isActive = panel.dataset.productMediaPanel === String(mediaId);
      panel.tabIndex = isActive ? 0 : -1;
    });

    if (options.syncVariant !== false) {
      syncProductVariantToMedia(gallery, mediaId);
    }

    gallery.dispatchEvent(new CustomEvent('quitchibo:product-media-change', {
      bubbles: true,
      detail: { mediaId },
    }));

    return true;
  };

  const syncProductGalleryToVariant = (select) => {
    const selectedOption = select?.selectedOptions?.[0];
    const mediaId = selectedOption?.dataset.variantFeaturedMediaId;

    if (!mediaId) return;

    const productLayout = select.closest('.q-product-layout');
    const gallery = productLayout?.querySelector('[data-product-gallery]');
    setProductGalleryActiveMedia(gallery, mediaId, { syncVariant: false });
  };

  const syncProductPriceToVariant = (select) => {
    const selectedOption = select?.selectedOptions?.[0];
    const productLayout = select?.closest('.q-product-layout');
    const priceContainer = productLayout?.querySelector('[data-product-price]');
    if (!selectedOption || !priceContainer) return;

    const priceCents = Number(selectedOption.dataset.variantPriceCents) || 0;
    const compareAtPriceCents = Number(selectedOption.dataset.variantCompareAtPriceCents) || 0;
    const priceText = selectedOption.dataset.variantPrice || formatMoney(priceCents);
    const compareAtPriceText = selectedOption.dataset.variantCompareAtPrice || '';
    const hasCompareAtPrice = compareAtPriceCents > priceCents && Boolean(compareAtPriceText);

    let currentPrice = priceContainer.querySelector('[data-product-price-current]');
    let compareAtPrice = priceContainer.querySelector('[data-product-price-compare]');

    if (!currentPrice) {
      currentPrice = document.createElement('span');
      currentPrice.setAttribute('data-product-price-current', '');
      priceContainer.prepend(currentPrice);
    }

    if (!compareAtPrice) {
      compareAtPrice = document.createElement('s');
      compareAtPrice.setAttribute('data-product-price-compare', '');
      priceContainer.append(compareAtPrice);
    }

    currentPrice.textContent = priceText;
    currentPrice.classList.toggle('q-product-info__sale', hasCompareAtPrice);
    compareAtPrice.textContent = hasCompareAtPrice ? compareAtPriceText : '';
    compareAtPrice.hidden = !hasCompareAtPrice;
  };

  const syncProductSubmitToVariant = (select) => {
    const selectedOption = select?.selectedOptions?.[0];
    const form = select?.closest('[data-product-form]');
    const submit = form?.querySelector('[data-product-submit]');
    if (!selectedOption || !form || !submit) return;

    const isAvailable = selectedOption.dataset.variantAvailable !== 'false';
    const label = submit.querySelector('span');
    const availableText = submit.dataset.addToCartText || label?.textContent || '';
    const soldOutText = submit.dataset.addToCartSoldOut || 'Ausverkauft';

    const gate = window.QProductFormState?.forForm(form);
    if (gate) gate.setAvailability(isAvailable);
    else {
      const guarded = form.dataset.productPreviewOnly === 'true' || form.dataset.colorWizardEnabled === 'true';
      submit.disabled = !isAvailable || guarded;
      submit.setAttribute('aria-disabled', String(submit.disabled));
    }
    form.classList.toggle('is-selected-variant-unavailable', !isAvailable);
    if (label && submit.getAttribute('aria-busy') !== 'true') label.textContent = isAvailable || form.dataset.productPreviewOnly === 'true' ? availableText : soldOutText;
  };

  const initProductGalleries = (root = document) => {
    root.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
      if (gallery.dataset.productGalleryReady === 'true') return;
      gallery.dataset.productGalleryReady = 'true';

      const thumbs = Array.from(gallery.querySelectorAll('[data-product-media-thumb]'));
      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => setProductGalleryActiveMedia(gallery, thumb.dataset.productMediaThumb));
      });
    });
  };

  const initProductCardHandMotion = (root = document) => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    root.querySelectorAll('[data-card-hand-motion] [data-product-gallery]').forEach((gallery) => {
      if (gallery.dataset.cardHandMotionReady === 'true') return;
      gallery.dataset.cardHandMotionReady = 'true';

      const product = gallery.closest('[data-card-hand-motion]');
      const intensity = Math.max(20, Math.min(100, Number(product?.dataset.cardHandIntensity) || 70)) / 100;
      const glare = Math.max(0, Math.min(100, Number(product?.dataset.cardHandGlare) || 55)) / 100;
      const returnSpeed = Math.max(20, Math.min(100, Number(product?.dataset.cardHandReturn) || 70)) / 100;
      const maxRotate = 6 + (intensity * 8);
      const maxShift = 2 + (intensity * 5);
      const followEase = 0.2 + (intensity * 0.08);
      const returnEase = 0.08 + (returnSpeed * 0.12);
      const ambientCache = new Map();

      let activeItem = null;
      let activePointer = null;
      let frame = null;
      const current = { rx: 0, ry: 0, sx: 0, sy: 0, gx: 50, gy: 50, go: 0, scale: 1 };
      const target = { rx: 0, ry: 0, sx: 0, sy: 0, gx: 50, gy: 50, go: 0, scale: 1 };

      const getActiveItem = () => gallery.querySelector('[data-product-media-panel].is-active');

      const rgbToCss = ({ r, g, b }) => `rgb(${Math.round(r)} ${Math.round(g)} ${Math.round(b)})`;

      const getImageAmbientColors = async (src) => {
        if (!src) return null;
        if (ambientCache.has(src)) return ambientCache.get(src);

        const promise = new Promise((resolve) => {
          const sample = new Image();
          sample.crossOrigin = 'anonymous';
          sample.decoding = 'async';
          sample.onload = () => {
            try {
              const canvas = document.createElement('canvas');
              const size = 24;
              canvas.width = size;
              canvas.height = size;
              const context = canvas.getContext('2d', { willReadFrequently: true });
              if (!context) {
                resolve(null);
                return;
              }

              context.drawImage(sample, 0, 0, size, size);
              const { data } = context.getImageData(0, 0, size, size);
              const colors = [];

              for (let index = 0; index < data.length; index += 4) {
                const alpha = data[index + 3];
                if (alpha < 80) continue;

                const r = data[index];
                const g = data[index + 1];
                const b = data[index + 2];
                const brightness = (r + g + b) / 3;
                const saturation = Math.max(r, g, b) - Math.min(r, g, b);
                if (brightness < 22 || brightness > 242 || saturation < 18) continue;

                colors.push({ r, g, b, score: saturation + Math.abs(brightness - 128) * 0.18 });
              }

              if (!colors.length) {
                resolve(null);
                return;
              }

              colors.sort((first, second) => second.score - first.score);
              const primary = colors[0];
              const secondary = colors.find((color) => (
                Math.abs(color.r - primary.r) + Math.abs(color.g - primary.g) + Math.abs(color.b - primary.b) > 82
              )) || colors[Math.min(colors.length - 1, Math.floor(colors.length * 0.38))] || primary;

              resolve({
                primary: rgbToCss(primary),
                secondary: rgbToCss(secondary),
              });
            } catch {
              resolve(null);
            }
          };
          sample.onerror = () => resolve(null);
          sample.src = src;
        });

        ambientCache.set(src, promise);
        return promise;
      };

      const syncAmbientFromImage = async (item) => {
        if (!item || item.dataset.cardAmbientReady === 'true') return;

        const image = item.querySelector('img');
        const src = image?.currentSrc || image?.src || '';
        if (!src) return;

        item.dataset.cardAmbientReady = 'true';
        const colors = await getImageAmbientColors(src);
        if (!colors || !item.isConnected) return;

        item.style.setProperty('--q-card-ambient-1', colors.primary);
        item.style.setProperty('--q-card-ambient-2', colors.secondary);
      };

      gallery.querySelectorAll('[data-product-media-panel]').forEach(syncAmbientFromImage);

      const setVars = (item) => {
        if (!item) return;
        item.style.setProperty('--q-card-hand-rotate-x', `${current.rx.toFixed(3)}deg`);
        item.style.setProperty('--q-card-hand-rotate-y', `${current.ry.toFixed(3)}deg`);
        item.style.setProperty('--q-card-hand-shift-x', `${current.sx.toFixed(3)}px`);
        item.style.setProperty('--q-card-hand-shift-y', `${current.sy.toFixed(3)}px`);
        item.style.setProperty('--q-card-hand-glare-x', `${current.gx.toFixed(2)}%`);
        item.style.setProperty('--q-card-hand-glare-y', `${current.gy.toFixed(2)}%`);
        item.style.setProperty('--q-card-hand-glare-opacity', current.go.toFixed(3));
        item.style.setProperty('--q-card-hand-scale', current.scale.toFixed(4));
      };

      const clearVars = (item) => {
        if (!item) return;
        item.style.removeProperty('--q-card-hand-rotate-x');
        item.style.removeProperty('--q-card-hand-rotate-y');
        item.style.removeProperty('--q-card-hand-shift-x');
        item.style.removeProperty('--q-card-hand-shift-y');
        item.style.removeProperty('--q-card-hand-glare-x');
        item.style.removeProperty('--q-card-hand-glare-y');
        item.style.removeProperty('--q-card-hand-glare-opacity');
        item.style.removeProperty('--q-card-hand-scale');
        item.classList.remove('is-card-hand-active');
      };

      const nearTarget = () => (
        Math.abs(current.rx - target.rx) < 0.015 &&
        Math.abs(current.ry - target.ry) < 0.015 &&
        Math.abs(current.sx - target.sx) < 0.015 &&
        Math.abs(current.sy - target.sy) < 0.015 &&
        Math.abs(current.go - target.go) < 0.01 &&
        Math.abs(current.scale - target.scale) < 0.001
      );

      const tick = () => {
        frame = null;
        const item = activeItem || getActiveItem();
        if (!item) return;

        const ease = activePointer === null ? returnEase : followEase;
        current.rx += (target.rx - current.rx) * ease;
        current.ry += (target.ry - current.ry) * ease;
        current.sx += (target.sx - current.sx) * ease;
        current.sy += (target.sy - current.sy) * ease;
        current.gx += (target.gx - current.gx) * ease;
        current.gy += (target.gy - current.gy) * ease;
        current.go += (target.go - current.go) * ease;
        current.scale += (target.scale - current.scale) * ease;

        if (activePointer === null && nearTarget()) {
          Object.assign(current, target);
          setVars(item);
          if (current.go === 0 && current.rx === 0 && current.ry === 0) item.classList.remove('is-card-hand-active');
          return;
        }

        setVars(item);
        frame = window.requestAnimationFrame(tick);
      };

      const schedule = () => {
        if (frame === null) frame = window.requestAnimationFrame(tick);
      };

      const setTargetFromPointer = (event, item) => {
        const rect = item.getBoundingClientRect();
        if (!rect.width || !rect.height) return;

        const px = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
        const py = Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height));
        const centeredX = px - 0.5;
        const centeredY = py - 0.5;

        target.ry = centeredX * maxRotate * 2;
        target.rx = centeredY * maxRotate * -2;
        target.sx = centeredX * maxShift;
        target.sy = centeredY * maxShift;
        target.gx = 100 - (px * 100);
        target.gy = py * 100;
        target.go = glare * 0.82;
        target.scale = 1.015 + (intensity * 0.012);
        schedule();
      };

      const resetTarget = () => {
        target.rx = 0;
        target.ry = 0;
        target.sx = 0;
        target.sy = 0;
        target.gx = 50;
        target.gy = 50;
        target.go = 0;
        target.scale = 1;
        schedule();
      };

      gallery.addEventListener('pointerdown', (event) => {
        if (event.button && event.button !== 0) return;

        const item = event.target.closest('[data-product-media-panel].is-active');
        if (!item || !gallery.contains(item)) return;

        activeItem = item;
        activePointer = event.pointerId;
        item.classList.add('is-card-hand-active');
        syncAmbientFromImage(item);
        item.setPointerCapture?.(event.pointerId);
        setTargetFromPointer(event, item);
        event.preventDefault();
      });

      gallery.addEventListener('pointermove', (event) => {
        if (activePointer !== event.pointerId || !activeItem) return;

        setTargetFromPointer(event, activeItem);
        event.preventDefault();
      });

      const release = (event) => {
        if (activePointer !== event.pointerId || !activeItem) return;

        activeItem.releasePointerCapture?.(event.pointerId);
        activePointer = null;
        resetTarget();
      };

      gallery.addEventListener('pointerup', release);
      gallery.addEventListener('pointercancel', release);
      gallery.addEventListener('quitchibo:product-media-change', () => {
        gallery.querySelectorAll('[data-product-media-panel]').forEach(clearVars);
        activeItem = null;
        activePointer = null;
        Object.assign(current, { rx: 0, ry: 0, sx: 0, sy: 0, gx: 50, gy: 50, go: 0, scale: 1 });
        Object.assign(target, current);
        syncAmbientFromImage(getActiveItem());
      });
    });
  };

  const initProductImageZoom = (root = document) => {
    let previousFocus = null;
    let productZoomScale = 1;
    let productZoomBaseDistance = null;
    let productZoomBaseScale = 1;
    let productZoomDragStart = null;
    const productZoomPointers = new Map();

    const getZoomImage = (zoom) => zoom?.querySelector('[data-product-zoom-image]');
    const clampZoomScale = (value) => Math.min(4, Math.max(1, value));
    const getProductZoomBaseWidth = () => Math.min(Math.max(window.innerWidth * 1.7, window.innerHeight * 1.04), 1380);
    const getPointerDistance = () => {
      const points = Array.from(productZoomPointers.values());
      if (points.length < 2) return null;
      return Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
    };
    const getPointerMidpoint = () => {
      const points = Array.from(productZoomPointers.values());
      if (!points.length) return null;
      const totals = points.reduce((sum, point) => ({
        x: sum.x + point.x,
        y: sum.y + point.y,
      }), { x: 0, y: 0 });
      return {
        x: totals.x / points.length,
        y: totals.y / points.length,
      };
    };
    const setProductZoomScale = (zoom, nextScale, origin = null) => {
      const frame = zoom?.querySelector('.q-product-zoom__frame');
      const image = getZoomImage(zoom);
      if (!frame || !image) return;

      const frameRect = frame.getBoundingClientRect();
      const focusX = origin ? origin.x - frameRect.left : frame.clientWidth / 2;
      const focusY = origin ? origin.y - frameRect.top : frame.clientHeight / 2;
      const ratioX = (frame.scrollLeft + focusX) / Math.max(1, frame.scrollWidth);
      const ratioY = (frame.scrollTop + focusY) / Math.max(1, frame.scrollHeight);

      productZoomScale = clampZoomScale(nextScale);
      image.style.setProperty('--q-product-zoom-image-width', `${Math.round(getProductZoomBaseWidth() * productZoomScale)}px`);
      image.closest('.q-product-zoom__frame')?.classList.toggle('is-dragging', Boolean(productZoomDragStart));

      window.requestAnimationFrame(() => {
        frame.scrollLeft = Math.max(0, (frame.scrollWidth * ratioX) - focusX);
        frame.scrollTop = Math.max(0, (frame.scrollHeight * ratioY) - focusY);
      });
    };
    const resetZoomTransform = (image) => {
      productZoomScale = 1;
      productZoomBaseDistance = null;
      productZoomBaseScale = 1;
      productZoomDragStart = null;
      productZoomPointers.clear();
      image?.style.setProperty('--q-product-zoom-image-width', `${Math.round(getProductZoomBaseWidth())}px`);
      image?.closest('.q-product-zoom__frame')?.classList.remove('is-dragging');
    };

    const closeZoom = () => {
      const zoom = document.querySelector('[data-product-zoom-modal]');
      if (!zoom || zoom.hidden) return;

      resetZoomTransform(getZoomImage(zoom));
      zoom.classList.remove('is-open');
      document.documentElement.classList.remove('q-product-zoom-open');
      window.setTimeout(() => {
        zoom.hidden = true;
        const image = getZoomImage(zoom);
        if (image) {
          image.onload = null;
          image.removeAttribute('src');
          image.alt = '';
        }
        previousFocus?.focus?.({ preventScroll: true });
        previousFocus = null;
      }, 220);
    };

    const bindZoomGestures = (zoom) => {
      const frame = zoom.querySelector('.q-product-zoom__frame');
      if (!frame || frame.dataset.productZoomGesturesReady === 'true') return;
      frame.dataset.productZoomGesturesReady = 'true';

      frame.addEventListener('wheel', (event) => {
        const image = getZoomImage(zoom);
        if (!image) return;

        event.preventDefault();
        setProductZoomScale(zoom, productZoomScale + (event.deltaY > 0 ? -0.18 : 0.18), {
          x: event.clientX,
          y: event.clientY,
        });
      }, { passive: false });

      frame.addEventListener('pointerdown', (event) => {
        const image = getZoomImage(zoom);
        if (!image) return;

        productZoomPointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        frame.setPointerCapture?.(event.pointerId);

        if (productZoomPointers.size === 1) {
          productZoomDragStart = {
            pointerId: event.pointerId,
            x: event.clientX,
            y: event.clientY,
            scrollLeft: frame.scrollLeft,
            scrollTop: frame.scrollTop,
          };
        }

        if (productZoomPointers.size === 2) {
          productZoomBaseDistance = getPointerDistance();
          productZoomBaseScale = productZoomScale;
          productZoomDragStart = null;
        }

        frame.classList.toggle('is-dragging', Boolean(productZoomDragStart));
        event.preventDefault();
      }, { passive: false });

      frame.addEventListener('pointermove', (event) => {
        if (!productZoomPointers.has(event.pointerId)) return;
        const image = getZoomImage(zoom);
        if (!image) return;

        productZoomPointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

        if (productZoomPointers.size >= 2 && productZoomBaseDistance) {
          const distance = getPointerDistance();
          if (distance) setProductZoomScale(zoom, productZoomBaseScale * (distance / productZoomBaseDistance), getPointerMidpoint());
        } else if (productZoomDragStart) {
          frame.scrollLeft = productZoomDragStart.scrollLeft - (event.clientX - productZoomDragStart.x);
          frame.scrollTop = productZoomDragStart.scrollTop - (event.clientY - productZoomDragStart.y);
        }

        event.preventDefault();
      }, { passive: false });

      const releasePointer = (event) => {
        if (!productZoomPointers.has(event.pointerId)) return;
        const image = getZoomImage(zoom);

        productZoomPointers.delete(event.pointerId);
        frame.releasePointerCapture?.(event.pointerId);
        productZoomBaseDistance = null;
        productZoomBaseScale = productZoomScale;

        if (productZoomPointers.size === 1) {
          const remaining = Array.from(productZoomPointers.entries())[0];
          productZoomDragStart = {
            pointerId: remaining[0],
            x: remaining[1].x,
            y: remaining[1].y,
            scrollLeft: frame.scrollLeft,
            scrollTop: frame.scrollTop,
          };
        } else {
          productZoomDragStart = null;
        }

        frame.classList.toggle('is-dragging', Boolean(productZoomDragStart));
      };

      frame.addEventListener('pointerup', releasePointer);
      frame.addEventListener('pointercancel', releasePointer);
    };

    const getOrCreateZoom = () => {
      let zoom = document.querySelector('[data-product-zoom-modal]');
      if (zoom) return zoom;

      zoom = document.createElement('div');
      zoom.className = 'q-product-zoom';
      zoom.hidden = true;
      zoom.setAttribute('data-product-zoom-modal', '');
      zoom.setAttribute('role', 'dialog');
      zoom.setAttribute('aria-modal', 'true');
      zoom.setAttribute('aria-label', 'Produktbild Zoom');
      zoom.innerHTML = `
        <button class="q-product-zoom__close" type="button" data-product-zoom-close aria-label="Zoom schliessen">
          <span aria-hidden="true">X</span>
        </button>
        <div class="q-product-zoom__surface">
          <div class="q-product-zoom__frame">
            <img class="q-product-zoom__image" data-product-zoom-image alt="">
          </div>
        </div>
      `;
      document.body.appendChild(zoom);
      bindZoomGestures(zoom);

      zoom.addEventListener('click', (event) => {
        if (event.target.closest('[data-product-zoom-close]') || event.target.classList.contains('q-product-zoom__surface')) closeZoom();
      });

      return zoom;
    };

    const centerZoomImage = (zoom) => {
      const frame = zoom?.querySelector('.q-product-zoom__frame');
      if (!frame) return;

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          frame.scrollLeft = Math.max(0, (frame.scrollWidth - frame.clientWidth) / 2);
          frame.scrollTop = Math.max(0, (frame.scrollHeight - frame.clientHeight) / 2);
        });
      });
    };

    const openZoom = (trigger) => {
      const src = trigger?.dataset.productZoomSrc || trigger?.querySelector('img')?.currentSrc || trigger?.querySelector('img')?.src;
      if (!src) return;

      const zoom = getOrCreateZoom();
      const image = getZoomImage(zoom);
      if (!image) return;

      previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : trigger;
      resetZoomTransform(image);
      image.onload = () => centerZoomImage(zoom);
      image.src = src;
      image.alt = trigger.dataset.productZoomAlt || trigger.querySelector('img')?.alt || '';
      zoom.hidden = false;
      document.documentElement.classList.add('q-product-zoom-open');
      window.requestAnimationFrame(() => {
        zoom.classList.add('is-open');
        centerZoomImage(zoom);
      });
      zoom.querySelector('[data-product-zoom-close]')?.focus?.({ preventScroll: true });
    };

    root.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
      if (gallery.dataset.productZoomReady === 'true') return;
      gallery.dataset.productZoomReady = 'true';

      let tapStart = null;

      gallery.addEventListener('pointerdown', (event) => {
        const trigger = event.target.closest('[data-product-zoom-trigger].is-active');
        if (!trigger || !gallery.contains(trigger)) {
          tapStart = null;
          return;
        }

        tapStart = {
          trigger,
          pointerId: event.pointerId,
          x: event.clientX,
          y: event.clientY,
          time: Date.now(),
        };
      }, true);

      gallery.addEventListener('pointerup', (event) => {
        if (!tapStart || tapStart.pointerId !== event.pointerId) return;

        const distance = Math.hypot(event.clientX - tapStart.x, event.clientY - tapStart.y);
        const duration = Date.now() - tapStart.time;
        const trigger = tapStart.trigger;
        tapStart = null;

        if (distance <= 9 && duration <= 520) {
          event.preventDefault();
          openZoom(trigger);
        }
      }, true);

      gallery.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        const trigger = event.target.closest('[data-product-zoom-trigger].is-active');
        if (!trigger || !gallery.contains(trigger)) return;

        event.preventDefault();
        openZoom(trigger);
      });
    });

    if (document.documentElement.dataset.productZoomKeyReady === 'true') return;
    document.documentElement.dataset.productZoomKeyReady = 'true';
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeZoom();
    });
  };

  const openCartDrawer = async (cartOverride) => {
    if (!drawer) return;

    drawer.hidden = false;
    document.documentElement.classList.add('q-drawer-open');
    window.requestAnimationFrame(() => drawer.classList.add('is-open'));

    try {
      renderCartDrawer(cartOverride || await fetchCart());
    } catch {
      if (drawerBody) drawerBody.innerHTML = `<div class="q-cart-drawer__empty"><p>${escapeHtml(window.QuitchiboCart?.error || 'Warenkorb konnte nicht geladen werden.')}</p></div>`;
    }

    drawerPanel?.focus();
  };

  const closeCartDrawer = () => {
    if (!drawer) return;

    drawer.classList.remove('is-open');
    document.documentElement.classList.remove('q-drawer-open');
    window.setTimeout(() => {
      drawer.hidden = true;
    }, 220);
  };

  const handleCartRemove = (event) => {
    const removeButton = event.target.closest('[data-cart-remove]');
    if (!removeButton || !drawer?.contains(removeButton)) return false;

    event.preventDefault();
    removeCartItem(removeButton);
    return true;
  };

  document.addEventListener('pointerdown', (event) => {
    if (event.button && event.button !== 0) return;
    handleCartRemove(event);
  });

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-cart-drawer-open]');
    if (opener && drawer) {
      event.preventDefault();
      openCartDrawer();
      return;
    }

    if (handleCartRemove(event)) return;

    if (event.target.closest('[data-cart-drawer-close]')) {
      closeCartDrawer();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeCartDrawer();
  });

  const setCustomerTextValidationState = (input, isValid) => {
    const field = input.closest('[data-customer-text-field]');
    const error = field?.querySelector('[data-customer-text-error-message]');

    field?.classList.toggle('is-invalid', !isValid);
    input.setAttribute('aria-invalid', isValid ? 'false' : 'true');
    if (error) error.hidden = isValid;
  };

  const validateCustomerTextInput = (input, focusInvalid = false) => {
    if (!input?.matches('[data-customer-text-input][required]')) return true;

    const isValid = input.value.trim().length > 0;
    setCustomerTextValidationState(input, isValid);

    if (!isValid && focusInvalid) {
      const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      input.focus({ preventScroll: true });
      input.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    return isValid;
  };

  const syncProductColorChoiceSelected = (group) => {
    const selectedInput = group?.querySelector('[data-product-color-choice-input]:checked');
    const selectedLabel = group?.querySelector('[data-product-color-choice-selected]');
    const selectedLabelValue = selectedInput?.dataset.colorChoiceLabel || selectedInput?.value || '';

    if (!selectedLabel) return;

    const selectedLabelTarget = selectedLabel.querySelector('span');
    if (selectedLabelTarget) selectedLabelTarget.textContent = selectedLabelValue;
    selectedLabel.hidden = !selectedLabelValue;
  };

  const setProductColorChoiceValidationState = (group, isValid) => {
    const error = group?.querySelector('[data-product-color-choice-error-message]');

    group?.classList.toggle('is-invalid', !isValid);
    group?.querySelectorAll('[data-product-color-choice-input]').forEach((input) => {
      input.setAttribute('aria-invalid', isValid ? 'false' : 'true');
    });
    if (error) error.hidden = isValid;
  };

  const validateProductColorChoice = (group, focusInvalid = false) => {
    if (!group?.matches('[data-product-color-choice][data-product-color-choice-required]')) {
      syncProductColorChoiceSelected(group);
      return true;
    }

    const selectedInput = group.querySelector('[data-product-color-choice-input]:checked');
    const isValid = Boolean(selectedInput);
    setProductColorChoiceValidationState(group, isValid);
    syncProductColorChoiceSelected(group);

    if (!isValid && focusInvalid) {
      const firstInput = group.querySelector('[data-product-color-choice-input]');
      const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      firstInput?.focus({ preventScroll: true });
      group.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    return isValid;
  };

  document.addEventListener('invalid', (event) => {
    const input = event.target.closest?.('[data-customer-text-input]');
    const colorChoiceInput = event.target.closest?.('[data-product-color-choice-input]');
    if (!input && !colorChoiceInput) return;

    event.preventDefault();

    if (input) {
      validateCustomerTextInput(input, true);
      return;
    }

    validateProductColorChoice(colorChoiceInput.closest('[data-product-color-choice]'), true);
  }, true);

  document.addEventListener('input', (event) => {
    const input = event.target.closest?.('[data-customer-text-input]');
    if (!input || input.getAttribute('aria-invalid') !== 'true') return;

    validateCustomerTextInput(input);
  });

  document.addEventListener('change', (event) => {
    const variantSelect = event.target.closest?.('[data-product-variant-select]');
    if (variantSelect) {
      syncProductGalleryToVariant(variantSelect);
      syncProductPriceToVariant(variantSelect);
      syncProductSubmitToVariant(variantSelect);
      return;
    }

    const input = event.target.closest?.('[data-product-color-choice-input]');
    if (!input) return;

    const group = input.closest('[data-product-color-choice]');
    syncProductColorChoiceSelected(group);
    if (group?.classList.contains('is-invalid')) validateProductColorChoice(group);
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-product-form]');
    if (!form || event.defaultPrevented) return;
    if (form.dataset.cartSubmitting === 'true') { event.preventDefault(); return; }
    const gate = form.querySelector('[data-product-submit]') ? window.QProductFormState?.forForm(form) : null;
    if (form.dataset.productPreviewOnly === 'true' || (form.dataset.colorWizardEnabled === 'true' && !gate) || (gate && !gate.canSubmit())) {
      event.preventDefault();
      form.dispatchEvent(new CustomEvent('q:product-submit-blocked'));
      return;
    }

    const variantSelect = form.querySelector('[data-product-variant-select]');
    const selectedVariant = variantSelect?.selectedOptions?.[0];
    if (selectedVariant?.dataset.variantAvailable === 'false') {
      event.preventDefault();
      syncProductSubmitToVariant(variantSelect);
      return;
    }

    const invalidCustomerText = [...form.querySelectorAll('[data-customer-text-input][required]')]
      .find((input) => !validateCustomerTextInput(input));

    if (invalidCustomerText) {
      event.preventDefault();
      validateCustomerTextInput(invalidCustomerText, true);
      return;
    }

    const invalidColorChoice = [...form.querySelectorAll('[data-product-color-choice][data-product-color-choice-required]')]
      .find((group) => !validateProductColorChoice(group));

    if (invalidColorChoice) {
      event.preventDefault();
      validateProductColorChoice(invalidColorChoice, true);
      return;
    }

    if (!drawer) return;

    event.preventDefault();

    form.dataset.cartSubmitting = 'true';
    const variantId = String(form.querySelector('[name="id"]')?.value || '');
    const submitter = event.submitter || form.querySelector('button[name="add"]');
    const submitterLabel = submitter?.querySelector('span');
    const defaultText = submitter?.dataset.addToCartText || submitterLabel?.textContent;
    const loadingText = submitter?.dataset.addToCartLoading || defaultText;

    if (gate) gate.setBusy(true);
    else if (submitter) {
      submitter.disabled = true;
      submitter.setAttribute('aria-busy', 'true');
    }
    if (submitterLabel && loadingText) submitterLabel.textContent = loadingText;

    try {
      const added = await addToCart(form);
      const cart = await fetchCart();
      await openCartDrawer(cart);
      const productHandle = added?.handle || cart.items?.find((item) => String(item.variant_id || item.id) === variantId)?.handle || form.dataset.catalogProductHandle || '';
      rememberCatalogCartChange(productHandle);
      form.dispatchEvent(new CustomEvent('quitchibo:cart-confirmed', { bubbles: true, detail: { variantId, productHandle, cart } }));
    } catch (error) {
      form.dispatchEvent(new CustomEvent('quitchibo:add-to-cart-error', {
        bubbles: true,
        detail: { message: error.message, variantId },
      }));
    } finally {
      delete form.dataset.cartSubmitting;
      if (gate) {
        gate.setBusy(false);
        syncProductSubmitToVariant(variantSelect);
        if (!variantSelect && submitterLabel && defaultText) submitterLabel.textContent = defaultText;
      } else {
        if (submitter) { submitter.disabled = false; submitter.removeAttribute('aria-busy'); }
        if (submitterLabel && defaultText) submitterLabel.textContent = defaultText;
      }
    }
  });

  initVacationMarquee(document);
  initVacationNotice(document);
  document.querySelectorAll('[data-header-menu]').forEach(initHeaderMenu);
  initHeroBrandMarks(document);
  initHeroHeadingBuild(document);
  initHeroBannerIntro(document);
  initProductGalleries(document);
  initProductImageZoom(document);
  initProductCardHandMotion(document);
  document.querySelectorAll('[data-product-variant-select]').forEach(syncProductSubmitToVariant);
  document.querySelectorAll('[data-product-color-choice]').forEach(syncProductColorChoiceSelected);
  document.querySelectorAll('[data-single-cards]').forEach(initSingleCards);
  syncShippingProgressFromDom();

  window.addEventListener('resize', () => scheduleHeroBrandMarkSync(document));
  document.fonts?.ready?.then(() => scheduleHeroBrandMarkSync(document));

  document.addEventListener('shopify:section:load', (event) => {
    initVacationMarquee(event.target);
    initVacationNotice(event.target);
    event.target.querySelectorAll('[data-header-menu]').forEach(initHeaderMenu);
    initHeroBrandMarks(event.target);
    initHeroHeadingBuild(event.target);
    initHeroBannerIntro(event.target);
    initProductGalleries(event.target);
    initProductImageZoom(event.target);
    initProductCardHandMotion(event.target);
    event.target.querySelectorAll('[data-product-variant-select]').forEach(syncProductSubmitToVariant);
    event.target.querySelectorAll('[data-single-cards]').forEach(initSingleCards);
    syncShippingProgressFromDom();
    event.target.dispatchEvent(new CustomEvent('quitchibo:section-ready', { bubbles: true }));
  });

  document.addEventListener('shopify:section:unload', (event) => {
    cleanupVacationNotices(event.target);
    event.target.querySelectorAll('[data-single-cards]').forEach((root) => root._disposeCatalogSearch?.());
  });
})();
